import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMyPageDashboard } from '../hooks/useMyPageDashboard';
import { SEB_TEST_DATA } from '../data/sebTestData';

const getSebProfileFromAnswers = (answers = {}) => {
    const sebScores = { S: 0, E: 0, F: 0 };
    const sebMaxScores = { S: 0, E: 0, F: 0 };

    SEB_TEST_DATA.questions.forEach((q) => {
        const answerIndex = answers[q.id];
        if (answerIndex === undefined) return;
        // Keep scoring direction consistent with MegaTest:
        // higher option index (0..3) => stronger tendency => higher score.
        const point = Number(answerIndex) * q.weight;
        sebScores[q.axis] += point;
        sebMaxScores[q.axis] += 3 * q.weight;
    });

    const normalize = (score, max) => (max ? Math.round((score / max) * 100) : 0);
    const percent = {
        S: normalize(sebScores.S, sebMaxScores.S),
        E: normalize(sebScores.E, sebMaxScores.E),
        F: normalize(sebScores.F, sebMaxScores.F)
    };
    const getLevel = (axis) => {
        // IMPORTANT: Use the same rounded % that the UI displays to avoid boundary mismatch (e.g. 66.6 -> 67).
        const p = percent[axis] ?? 0;
        if (p >= 67) return 'H';
        if (p >= 34) return 'M';
        return 'L';
    };
    const typeCode = `${getLevel('S')}${getLevel('E')}${getLevel('F')}`;

    return { percent, typeCode };
};


const EnglishPaidReport = ({ profile, onBack }) => {
    if (!profile) {
        return (
            <div style={styles.container}>
                <div style={styles.content}>
                    <button onClick={onBack} style={styles.backButton}>Back</button>
                    <h1 style={styles.headerTitle}>Premium Report</h1>
                    <p style={styles.headerSubtitle}>Complete the Mega Synergy test to generate this report.</p>
                </div>
            </div>
        );
    }

    const axes = [
        { key: 'S', name: 'Stability', high: 'You steady the emotional pace and organize uncertainty.', mid: 'You balance stability with flexibility.', low: 'You respond quickly to changing emotional currents.' },
        { key: 'E', name: 'Empathy', high: 'You notice emotional signals and respond with care.', mid: 'You consider emotion and context together.', low: 'You prefer facts, structure, and direct communication.' },
        { key: 'F', name: 'Belief', high: 'You value trust, consistency, and commitment.', mid: 'You judge relationships according to context.', low: 'You value autonomy and clear personal boundaries.' },
    ];
    const band = (score) => score >= 67 ? 'High' : score >= 34 ? 'Mid' : 'Low';
    const summary = 'Type ' + profile.typeCode + ' combines your stability, empathy, and relationship-belief patterns. Use the axis notes below as tendencies rather than fixed labels.';

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <button onClick={onBack} style={styles.backButton}>Back</button>
                <h1 style={styles.headerTitle}>Premium Personality Report</h1>
                <p style={styles.headerSubtitle}>{summary}</p>
                <div style={styles.mainCard}>
                    <div style={styles.badge}>TYPE {profile.typeCode}</div>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                        {axes.map((axis) => {
                            const score = profile.percent[axis.key] || 0;
                            const level = band(score);
                            const description = level === 'High' ? axis.high : level === 'Mid' ? axis.mid : axis.low;
                            return (
                                <section key={axis.key} style={styles.deepReportCard}>
                                    <div style={styles.deepReportTitle}>{axis.name} · {level}</div>
                                    <div style={{ height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 4, overflow: 'hidden', margin: '0.75rem 0' }}>
                                        <div style={{ width: score + '%', height: '100%', background: '#60a5fa' }} />
                                    </div>
                                    <strong>{score}%</strong>
                                    <p style={styles.deepReportText}>{description}</p>
                                </section>
                            );
                        })}
                    </div>
                    <section style={styles.deepReportCard}>
                        <div style={styles.deepReportTitle}>Practical Guidance</div>
                        <p style={styles.deepReportText}>Name your preferred communication pace before conflict begins. Ask what the other person needs instead of assuming, and make expectations explicit when a relationship matters.</p>
                    </section>
                </div>
            </div>
        </div>
    );
};

const PaidVersion = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { stats, loading } = useMyPageDashboard();
    const [sebProfile, setSebProfile] = useState(null);
    const [activeAxis, setActiveAxis] = useState('stability');
    const [activeDeepSection, setActiveDeepSection] = useState('1');
    const [socialSummaryOpen, setSocialSummaryOpen] = useState(false);
    const lang = i18n.language.startsWith('en') ? 'en' : 'ko';
    const AXIS_WORDS = {
        stability: {
            title: 'S축 (안정 / 균형 / 조율)',
            scoreKey: 'S',
            levels: {
                high: {
                    label: 'S High',
                    subtitle: '관계를 안정시키는 방향으로 움직이는 성향',
                    impression: '관계의 중심을 잡는 사람',
                    adjectives: [
                        '안정적인', '차분한', '침착한', '균형 잡힌', '중심을 잡는',
                        '조율하는', '정리하는', '흔들리지 않는', '신중한', '안정 지향적인',
                        '균형 감각이 있는', '상황을 가라앉히는', '무게감 있는', '버팀목 같은',
                        '갈등을 정리하는', '중심적인'
                    ]
                },
                mid: {
                    label: 'S Mid',
                    subtitle: '상황에 맞게 균형을 맞추는 성향',
                    impression: '상황에 맞게 균형을 맞추는 사람',
                    adjectives: [
                        '유연한', '균형적인', '자연스러운', '상황을 보는', '조정 가능한',
                        '무난한', '적응적인', '부드러운', '조화로운', '안정과 변화를 모두 보는',
                        '유동적인'
                    ]
                },
                low: {
                    label: 'S Low',
                    subtitle: '안정 유지보다 흐름에 반응하는 성향',
                    impression: '관계 흐름에 자연스럽게 반응하는 사람',
                    adjectives: [
                        '즉흥적인', '반응적인', '변화에 열려 있는', '흐름을 따르는', '가벼운',
                        '유동적인', '직관적인', '순간적인', '감정이 바로 나타나는', '활발한',
                        '에너지 있는'
                    ]
                }
            }
        },
        empathy: {
            title: 'E축 (이해 / 공감 / 감정 인식)',
            scoreKey: 'E',
            levels: {
                high: {
                    label: 'E High',
                    subtitle: '타인의 감정을 깊게 읽는 성향',
                    impression: '사람 마음을 잘 이해하는 사람',
                    adjectives: [
                        '공감적인', '세심한', '이해력 있는', '감수성이 높은', '눈치 빠른',
                        '배려 깊은', '따뜻한', '사람을 잘 보는', '마음을 읽는', '섬세한',
                        '민감한', '감정에 밝은', '정서적인'
                    ]
                },
                mid: {
                    label: 'E Mid',
                    subtitle: '감정과 상황을 함께 보는 성향',
                    impression: '감정과 상황을 함께 보는 사람',
                    adjectives: [
                        '균형적인', '현실적인', '상황을 보는', '합리적인', '조정하는',
                        '무난한', '객관적인', '안정적인', '차분한', '판단적인'
                    ]
                },
                low: {
                    label: 'E Low',
                    subtitle: '감정보다 사실과 구조 중심',
                    impression: '감정보다 상황을 보는 사람',
                    adjectives: [
                        '담백한', '직선적인', '단순한', '솔직한', '사실적인', '직접적인',
                        '현실적인', '논리적인', '명확한', '거리 두는', '감정에 휘둘리지 않는'
                    ]
                }
            }
        },
        belief: {
            title: 'B축 (신뢰 / 관계 믿음)',
            // NOTE: Existing SEB payload uses F slot; UI labels it as Belief (B).
            scoreKey: 'F',
            levels: {
                high: {
                    label: 'B High',
                    subtitle: '관계와 사람을 신뢰하는 성향',
                    impression: '믿고 맡길 수 있는 사람',
                    adjectives: [
                        '믿음직한', '책임감 있는', '신뢰하는', '헌신적인', '성실한',
                        '일관된', '관계 중심적인', '약속을 중시하는', '든든한', '안정적인',
                        '신중한', '깊이 있는'
                    ]
                },
                mid: {
                    label: 'B Mid',
                    subtitle: '관계를 상황에 따라 판단',
                    impression: '상황을 보며 관계하는 사람',
                    adjectives: [
                        '현실적인', '판단적인', '균형 잡힌', '유연한', '상황을 보는',
                        '적응적인', '조정적인', '합리적인', '신중한'
                    ]
                },
                low: {
                    label: 'B Low',
                    subtitle: '관계에 대한 기대와 의존이 낮음',
                    impression: '관계에서 자유로운 사람',
                    adjectives: [
                        '독립적인', '자유로운', '거리 두는', '가벼운', '기대하지 않는',
                        '자율적인', '느슨한', '부담 없는', '개인적인'
                    ]
                }
            }
        }
    };
    const SOCIAL_TYPE_SUMMARY_BY_CODE = {
        HHH: {
            title: 'HHH 유형 (정리 버전)',
            body: [
                '안정적인 중심을 유지하면서 타인의 감정을 이해할 수 있는 유형입니다.',
                '',
                '이 유형은 관계가 흔들리는 상황에서도 쉽게 감정에 휘둘리기보다는 차분하게 자신의 균형을 유지하려는 경향이 있습니다. 동시에 타인의 감정을 비교적 잘 이해하는 편이라 상대의 상태를 파악하고 상황을 안정적으로 정리하려는 모습을 보일 수 있습니다.',
                '',
                '또한 관계에 대한 기준이나 원칙이 비교적 분명하기 때문에 상대의 감정에 그대로 끌려가기보다는 자신의 중심을 유지한 상태에서 관계를 바라보는 경우가 많습니다. 그래서 감정적인 상황에서도 자신의 상태를 비교적 안정적으로 유지하면서 상대의 감정이 지나치게 흔들리지 않도록 도와주는 역할을 할 때도 있습니다.',
                '',
                '주변에서는 이 사람을 차분하면서도 믿을 수 있는 사람, 또는 관계의 중심을 잡는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        HHL: {
            title: 'HHL 유형',
            body: [
                '사람의 감정을 이해하지만 관계를 중심에 두지는 않는 사람',
                '',
                '이 유형은 타인의 감정이나 분위기를 비교적 잘 이해하는 편이며, 감정적으로 흔들리는 상황에서도 자신의 상태를 크게 무너뜨리지 않고 차분하게 바라보려는 경향이 있습니다.',
                '',
                '다만 관계 자체를 중요한 중심으로 두기보다는 자신의 기준이나 상태를 우선하는 편에 가까울 수 있습니다. 그래서 사람의 감정을 이해하면서도 관계를 적극적으로 이끌거나 분위기를 조율하려 하기보다는, 상황을 일정한 거리에서 바라보는 모습을 보일 때가 많습니다.',
                '',
                '이러한 이유로 이 유형은 사람들과의 감정적인 이해는 비교적 잘 이루어지지만, 관계를 유지하거나 분위기를 만들어가는 역할까지 맡으려 하지는 않는 경우가 많습니다.',
                '',
                '주변에서는 이 사람을 사람을 이해하지만 관계에 크게 얽매이지 않는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        HHM: {
            title: 'HHM 유형',
            body: [
                '관계의 흐름을 보며 거리를 조절하는 사람',
                '',
                '이 유형은 사람의 감정이나 분위기 변화를 비교적 잘 알아차리는 편이며, 관계가 크게 흔들리지 않도록 차분하게 상황을 바라보려는 경향이 있습니다. 감정적인 상황에서도 바로 반응하기보다는 상황을 정리하고 관계의 흐름을 안정적으로 유지하려는 모습을 보일 때가 많습니다.',
                '',
                '다만 관계를 항상 같은 방식으로 유지하려 하기보다는 상황과 관계의 흐름을 보며 거리나 태도를 조절하는 편에 가깝습니다. 어떤 관계에서는 자연스럽게 가까워질 수 있지만, 어떤 상황에서는 일정한 거리를 두는 것이 더 적절하다고 판단하기도 합니다.',
                '',
                '그래서 이 유형은 관계를 무조건 붙잡거나 끊어내기보다는 사람과 상황을 함께 보며 관계의 균형을 조정하는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 관계의 흐름을 보며 거리를 조절하는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        HLH: {
            title: 'HLH 유형 (수정)',
            body: [
                '감정을 인지하지만 자신의 기준과 선을 유지하는 사람',
                '',
                '이 유형은 감정적인 상황에서도 자신의 상태가 크게 흔들리지 않도록 차분하게 균형을 유지하려는 경향이 있습니다. 타인의 감정을 완전히 무시하는 것은 아니지만, 감정에 깊이 공감하기보다는 상대의 상태를 인지하는 정도에서 머무르는 경우가 많습니다.',
                '',
                '또한 관계에서 자신만의 기준이나 선을 비교적 중요하게 생각하는 편입니다. 그래서 상대의 감정에 그대로 끌려가기보다는 자신이 정해 둔 기준 안에서 관계를 유지하려는 모습이 나타날 수 있습니다.',
                '',
                '이러한 이유로 이 유형은 감정적인 분위기에 쉽게 휘둘리기보다는 일정한 거리에서 상황을 바라보며 관계를 이어가는 경우가 많습니다.',
                '',
                '주변에서는 이 사람을 차분하고 자기 기준이 분명한 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        HLM: {
            title: 'HLM 유형',
            body: [
                '분위기를 읽지만 감정에 깊이 들어가지는 않는 사람',
                '',
                '이 유형은 감정적인 상황에서도 자신의 상태를 비교적 안정적으로 유지하려는 경향이 있습니다. 주변의 분위기나 사람들의 감정 변화를 어느 정도 알아차리지만, 그 감정에 깊이 공감하거나 같이 휘말리기보다는 상황을 한 발 떨어져서 바라보는 경우가 많습니다.',
                '',
                '그래서 타인의 감정을 전혀 보지 않는 것은 아니지만, 감정에 크게 영향을 받기보다는 지금 어떤 상황이 벌어지고 있는지를 먼저 파악하려는 모습이 나타날 수 있습니다.',
                '',
                '또한 관계에서 강한 원칙을 밀어붙이기보다는 상황과 흐름을 보며 관계의 거리를 조절하는 편에 가깝습니다. 어떤 상황에서는 자연스럽게 가까워질 수 있지만, 어떤 경우에는 일정한 거리를 두는 것이 더 편하다고 느끼기도 합니다.',
                '',
                '이러한 이유로 이 유형은 감정적인 분위기에 쉽게 휘둘리기보다는 자신의 상태를 안정적으로 유지하면서 상황에 맞게 관계를 이어가는 경우가 많습니다.',
                '',
                '주변에서는 이 사람을 차분하고 상황을 보며 행동하는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        HLL: {
            title: 'HLL 유형',
            body: [
                '분위기를 인지하지만 관계에 얽매이지 않는 사람',
                '',
                '이 유형은 감정적인 상황에서도 자신의 상태가 쉽게 흔들리지 않는 편입니다. 주변의 분위기나 사람들의 감정 변화를 어느 정도 알아차리기는 하지만, 그 감정에 깊이 공감하거나 함께 휘말리기보다는 상황을 일정한 거리에서 바라보는 경우가 많습니다.',
                '',
                '또한 관계나 약속, 역할에 강하게 묶이기보다는 자유로운 상태를 유지하는 것을 더 편하게 느끼는 편입니다. 관계가 자연스럽게 이어지는 것은 괜찮지만, 특정한 관계나 역할에 계속 묶여 있어야 한다고 느껴질 때는 부담을 느낄 수도 있습니다.',
                '',
                '그래서 이 유형은 사람들과의 관계를 완전히 끊어내는 것은 아니지만 자신의 상태를 유지한 채 비교적 자유로운 거리에서 관계를 이어가는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 감정에 크게 휘둘리지 않으면서도 관계에 얽매이지 않는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        MHH: {
            title: 'MHH 유형',
            body: [
                '사람의 감정을 깊이 이해하고 관계를 책임 있게 이어가려는 사람',
                '',
                '이 유형은 타인의 감정이나 분위기에 비교적 잘 공감하는 편이며, 관계에서 신뢰나 약속 같은 요소를 중요하게 생각하는 경우가 많습니다. 그래서 관계가 흔들리거나 문제가 생기면 그냥 넘기기보다는 상황을 이해하고 해결하려는 방향으로 움직이려 할 때가 있습니다.',
                '',
                '또한 관계에서 서로의 역할이나 책임이 어느 정도 유지되는 것을 중요하게 생각하는 편입니다. 그래서 관계가 일방적으로 흐르거나 신뢰가 깨지는 상황이 생기면 불편함을 느낄 수도 있습니다.',
                '',
                '다만 감정 상황에서 완전히 흔들리지 않는 유형은 아니기 때문에, 관계 속에서 발생하는 감정 변화에 영향을 받기도 합니다. 그래서 상대의 감정을 이해하면서도 때로는 그 감정에 영향을 받아 고민하거나 생각이 많아질 수 있습니다.',
                '',
                '주변에서는 이 사람을 사람의 감정을 잘 이해하면서 관계를 책임감 있게 대하는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        MHM: {
            title: 'MHM 유형',
            body: [
                '사람의 감정을 잘 이해하지만 관계 속에서 고민이 많아질 수 있는 사람',
                '',
                '이 유형은 다른 사람의 감정이나 분위기를 비교적 잘 이해하는 편입니다. 그래서 관계 속에서 상대의 입장이나 감정을 고려하며 행동하려는 경우가 많습니다.',
                '',
                '다만 자신의 감정 상태가 항상 안정적으로 유지되는 유형은 아니기 때문에, 관계 속에서 발생하는 감정이나 상황에 영향을 받기도 합니다. 상대의 감정이나 관계의 흐름을 중요하게 생각하다 보니 사람이나 관계 때문에 고민이 많아질 수 있습니다.',
                '',
                '또한 관계에서 일정한 기준이나 신뢰를 중요하게 생각하기도 하지만, 상황에 따라 생각이 바뀌거나 관계에 대한 판단이 흔들릴 수도 있습니다. 그래서 어떤 관계에서는 가까워지고, 어떤 상황에서는 거리를 두는 등 관계 속에서 균형을 찾으려는 과정이 반복되기도 합니다.',
                '',
                '주변에서는 이 사람을 사람의 감정을 잘 이해하고 관계를 중요하게 생각하는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        MHL: {
            title: 'MHL 유형',
            body: [
                '사람의 감정을 잘 이해하지만 관계에 크게 얽매이지 않는 사람',
                '',
                '이 유형은 다른 사람의 감정이나 분위기를 비교적 잘 이해하는 편입니다. 그래서 대화나 관계 속에서 상대의 감정을 공감하거나 이해하려는 모습을 보일 때가 많습니다.',
                '',
                '다만 자신의 감정 상태가 항상 안정적으로 유지되는 유형은 아니기 때문에 관계 속에서 생기는 감정이나 상황에 영향을 받기도 합니다. 그래서 사람이나 관계 때문에 고민이 생길 수도 있지만, 동시에 특정 관계나 약속에 계속 묶여 있는 것을 부담스럽게 느낄 수도 있습니다.',
                '',
                '이러한 이유로 이 유형은 관계 속에서 감정을 이해하고 공감하기는 하지만, 관계를 반드시 유지해야 한다거나 책임져야 한다고 느끼기보다는 자연스럽게 이어지는 흐름을 더 중요하게 보는 경우가 많습니다.',
                '',
                '그래서 어떤 관계에서는 가까워질 수 있지만, 어떤 상황에서는 비교적 쉽게 거리를 두거나 관계를 정리하기도 합니다.',
                '',
                '주변에서는 이 사람을 사람의 감정을 잘 이해하지만 관계에 크게 얽매이지 않는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        MLH: {
            title: 'MLH 유형',
            body: [
                '감정보다는 기준과 상황을 먼저 보는 사람',
                '',
                '이 유형은 다른 사람의 감정을 깊이 공감하기보다는 상대의 상태나 상황을 인지하는 편에 가깝습니다. 그래서 감정적인 분위기 속에서도 바로 감정적으로 반응하기보다는 상황을 먼저 이해하려는 모습을 보일 때가 많습니다.',
                '',
                '또한 관계에서 약속이나 기준 같은 요소를 비교적 중요하게 생각하는 편입니다. 그래서 관계가 유지되기 위해서는 서로 지켜야 할 선이나 책임이 있다고 느끼는 경우가 많습니다.',
                '',
                '다만 감정 상황에서 완전히 영향을 받지 않는 유형은 아니기 때문에, 관계 속에서 발생하는 문제나 갈등을 보면서 내면에서 판단이나 고민이 생길 수 있습니다. 하지만 이러한 고민이 겉으로 크게 드러나기보다는 자신 안에서 정리되는 경우가 많습니다.',
                '',
                '그래서 이 유형은 감정적인 반응을 크게 드러내지는 않지만, 상황과 기준을 함께 보며 관계를 판단하는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 감정 표현은 많지 않지만 기준이 분명한 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        MLM: {
            title: 'MLM 유형',
            body: [
                '감정보다 상황을 보며 관계를 조절하는 사람',
                '',
                '이 유형은 사람의 감정에 깊게 들어가기보다는, 지금 어떤 분위기인지와 상황이 어떻게 흘러가는지를 먼저 보는 편입니다. 상대의 감정 변화를 아예 못 보는 것은 아니지만, 그 감정에 같이 휩쓸리기보다는 현재 상황을 파악하고 어떻게 반응하는 게 적절한지 판단하려는 경향이 있습니다.',
                '',
                '또한 관계를 무조건 유지하거나 쉽게 끊는 쪽도 아닙니다. 관계를 하나의 고정된 기준으로 보기보다는 상황과 흐름을 보며 지금은 가까이 갈지, 어느 정도 거리를 둘지를 조정하는 편에 가깝습니다.',
                '',
                '그래서 이 유형은 감정적인 공감이나 확고한 원칙으로 관계를 끌고 가기보다는, 상황을 읽고 무리 없는 선에서 관계를 운영하는 모습을 보일 때가 많습니다. 감정 표현이 크지 않아 가볍게 보일 수 있지만, 실제로는 관계를 즉흥적으로 다루기보다 나름의 판단 속에서 움직이는 경우가 많습니다.',
                '',
                '주변에서는 이 사람을 상황을 보며 무리 없이 관계를 이어가는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        MMH: {
            title: 'MMH 유형',
            body: [
                '관계를 신뢰와 기준 위에서 유지하려는 사람',
                '',
                '이 유형은 사람의 감정을 깊이 공감하는 편은 아니지만, 그렇다고 감정을 무시하는 유형도 아닙니다. 상대의 감정이나 상황을 어느 정도 이해하면서도 지금 관계가 어떤 상태인지와 서로의 역할을 함께 보려는 경향이 있습니다.',
                '',
                '또한 관계에서 신뢰나 약속 같은 기준을 비교적 중요하게 생각하는 편입니다. 그래서 관계가 지속되기 위해서는 서로 지켜야 할 선이나 책임이 어느 정도 존재해야 한다고 느끼는 경우가 많습니다.',
                '',
                '감정 상황에서 완전히 흔들리지 않는 유형은 아니기 때문에 관계 속에서 고민이 생길 수는 있지만, 감정에 휩쓸리기보다는 관계의 기준이나 신뢰가 유지되고 있는지에 더 관심을 두는 편입니다.',
                '',
                '그래서 이 유형은 관계를 감정 중심으로 끌고 가기보다는 신뢰와 기준이 유지되는 방향으로 관계를 이어가려는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 감정 표현은 과하지 않지만 신뢰할 수 있는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        HMH: {
            title: 'HMH 유형',
            body: [
                '관계에서 균형을 잡는 사람',
                '',
                '이 유형은 관계가 감정적으로 한쪽으로 치우치는 상황에서 분위기를 다시 정리하는 역할을 하는 경우가 많습니다. 상대의 감정이나 상황을 이해하려는 편이지만, 동시에 관계가 계속 이어지기 위해서는 서로 지켜야 할 기준도 필요하다고 느끼는 경우가 많습니다.',
                '',
                '그래서 대화가 감정적으로 흘러갈 때는 상황을 정리하려 하고, 관계가 느슨해질 때는 서로의 역할이나 책임을 다시 확인하려는 모습을 보이기도 합니다.',
                '',
                '주변에서는 이 사람을 이야기를 하면 정리가 되는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        HML: {
            title: 'HML 유형',
            body: [
                '사람을 이해하지만 거리를 유지하는 사람',
                '',
                '이 유형은 사람의 감정이나 분위기를 비교적 잘 파악하는 편입니다. 하지만 감정을 함께 끌어안기보다는 자신의 상태를 유지한 채 상황을 바라보는 경우가 많습니다.',
                '',
                '그래서 누군가 감정적으로 힘들어할 때 바로 개입하기보다는 필요한 순간에만 조용히 반응하는 편에 가깝습니다. 관계를 무겁게 책임져야 한다고 느끼기보다는 자연스럽게 이어지는 관계를 편하게 생각하는 경우가 많습니다.',
                '',
                '주변에서는 이 사람을 편안하지만 거리를 지킬 줄 아는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        MLL: {
            title: 'MLL 유형',
            body: [
                '관계를 가볍게 유지하는 사람',
                '',
                '이 유형은 사람의 감정을 어느 정도 알아차리기는 하지만, 그 감정에 깊이 들어가기보다는 분위기를 가볍게 넘기는 편에 가깝습니다.',
                '',
                '그래서 갈등이나 감정적인 상황이 생기면 문제를 깊게 파고들기보다는 분위기를 바꾸거나 자연스럽게 지나가게 두는 선택을 하는 경우가 많습니다.',
                '',
                '주변에서는 이 사람을 함께 있으면 편한 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        LHH: {
            title: 'LHH 유형',
            body: [
                '사람들의 감정에 바로 반응하는 사람',
                '',
                '이 유형은 다른 사람의 감정이나 분위기에 비교적 빠르게 반응하는 편입니다. 누군가 기쁘거나 힘들어하는 모습을 보면 그 감정이 바로 전달되는 느낌을 받을 수 있습니다. 그래서 관계 속에서 감정의 변화가 생기면 가만히 보기보다는 직접 반응하거나 개입하는 경우가 많습니다.',
                '',
                '또한 관계에서 신뢰나 약속 같은 요소를 중요하게 생각하는 편입니다. 그래서 가까운 사람들과의 관계에서는 서로 지켜야 할 선이나 책임이 있다고 느끼는 경우가 많습니다.',
                '',
                '이 때문에 감정적인 상황에서 누군가를 지지하거나 편을 들어주는 모습을 보이기도 합니다. 때로는 이런 태도가 관계를 더 단단하게 만들기도 하지만, 상황에 따라 감정적으로 깊이 들어가게 되는 경우도 있습니다.',
                '',
                '주변에서는 이 사람을 감정 표현이 분명하고 사람 편에 서는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        LHM: {
            title: 'LHM 유형',
            body: [
                '사람의 감정에 깊이 반응하지만 관계는 선택적으로 유지하는 사람',
                '',
                '이 유형은 사람의 감정이나 분위기에 비교적 민감하게 반응하는 편입니다. 누군가 기쁘거나 힘들어하는 모습을 보면 그 감정이 자연스럽게 전달되는 경우가 많고, 그래서 대화나 상황 속에서 감정적으로 깊이 공감하는 모습을 보이기도 합니다.',
                '',
                '하지만 모든 관계를 같은 방식으로 유지하려 하지는 않습니다. 관계가 자신에게 의미 있다고 느껴질 때는 가까워지고 깊이 관여하기도 하지만, 그렇지 않은 관계에서는 비교적 자연스럽게 거리를 두는 선택을 하기도 합니다.',
                '',
                '그래서 이 유형은 사람의 감정을 잘 이해하고 공감하면서도, 동시에 어떤 관계에 더 마음을 쓰고 어떤 관계에서는 거리를 둘지를 스스로 정하는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 사람에게 진심이지만 관계를 선택할 줄 아는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        LHL: {
            title: 'LHL 유형',
            body: [
                '사람의 감정에 자연스럽게 반응하는 사람',
                '',
                '이 유형은 다른 사람의 감정이나 분위기를 비교적 빠르게 느끼는 편입니다. 누군가 기쁘거나 힘들어하는 모습을 보면 그 감정이 바로 전달되는 느낌을 받을 수 있고, 그래서 대화나 상황 속에서 감정적으로 바로 반응하는 모습을 보이기도 합니다.',
                '',
                '또한 관계를 일정한 기준이나 책임으로 유지하기보다는 지금 느끼는 감정이나 분위기를 더 중요하게 생각하는 경우가 많습니다. 그래서 어떤 순간에는 사람들과 빠르게 가까워질 수 있고, 또 어떤 상황에서는 자연스럽게 거리를 두기도 합니다.',
                '',
                '이 유형은 관계를 무겁게 유지하려 하기보다는 감정이 흐르는 방향에 따라 사람들과 어울리는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 감정 표현이 자연스럽고 함께 있으면 분위기가 살아나는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        LMH: {
            title: 'LMH 유형',
            body: [
                '감정에는 빠르게 반응하지만 관계는 쉽게 가볍게 보지 않는 사람',
                '',
                '이 유형은 사람의 감정이나 분위기에 비교적 빠르게 반응하는 편입니다. 그래서 누군가 기쁘거나 힘들어하는 상황을 보면 감정적으로 바로 반응하거나 상황에 영향을 받는 경우가 많습니다.',
                '',
                '하지만 관계를 가볍게 보는 유형은 아닙니다. 가까운 관계에서는 서로 지켜야 할 신뢰나 약속이 있다고 생각하는 경우가 많고, 그래서 관계가 유지되기 위해서는 서로 책임이 필요하다고 느끼는 편입니다.',
                '',
                '이 때문에 감정적인 상황에서 바로 반응하기도 하지만, 동시에 이 관계가 어떤 관계인지에 대해 생각하는 모습을 보일 때가 많습니다. 감정만으로 관계를 판단하기보다는 신뢰가 유지되고 있는지를 함께 보는 경우가 많습니다.',
                '',
                '주변에서는 이 사람을 감정 표현은 분명하지만 관계를 가볍게 보지 않는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        LMM: {
            title: 'LMM 유형',
            body: [
                '감정에는 빠르게 반응하지만 관계의 거리는 스스로 조절하는 사람',
                '',
                '이 유형은 사람의 감정이나 분위기에 비교적 빠르게 반응하는 편입니다. 그래서 대화나 관계 속에서 감정적인 변화가 생기면 그 분위기에 영향을 받거나 바로 반응하는 모습을 보일 때가 많습니다.',
                '',
                '하지만 모든 관계에 깊이 들어가는 유형은 아닙니다. 사람들과 어울리는 것은 자연스럽게 하지만, 동시에 어떤 관계에서는 가까워지고 어떤 관계에서는 거리를 두는 선택을 하기도 합니다.',
                '',
                '그래서 이 유형은 감정적인 반응이 비교적 분명하게 나타나지만, 관계 자체는 상황과 사람에 따라 유연하게 조절하는 모습을 보일 때가 많습니다.',
                '',
                '어떤 순간에는 분위기를 함께 끌어올리기도 하고, 또 어떤 상황에서는 자연스럽게 빠져나오기도 합니다.',
                '',
                '주변에서는 이 사람을 감정 표현이 자연스럽고 사람들과 편하게 어울리는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        LML: {
            title: 'LML 유형',
            body: [
                '분위기에 자연스럽게 반응하며 사람들과 어울리는 사람',
                '',
                '이 유형은 사람의 감정이나 분위기에 비교적 빠르게 반응하는 편입니다. 그래서 누군가 기쁘거나 힘들어하는 상황이 생기면 그 분위기에 자연스럽게 영향을 받거나 바로 반응하는 모습을 보일 때가 많습니다.',
                '',
                '다만 관계를 일정한 기준이나 책임으로 유지해야 한다고 느끼기보다는 지금의 분위기와 상황에 맞게 사람들과 어울리는 것을 더 편하게 느끼는 경우가 많습니다.',
                '',
                '그래서 어떤 관계에서는 금방 가까워질 수 있고, 또 어떤 상황에서는 자연스럽게 거리를 두기도 합니다. 관계를 무겁게 유지하려 하기보다는 지금 함께 있는 사람들과 편안하게 어울리는 것을 중요하게 생각하는 편입니다.',
                '',
                '주변에서는 이 사람을 함께 있으면 분위기가 자연스럽게 살아나는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        LLH: {
            title: 'LLH 유형',
            body: [
                '감정은 느끼지만 판단은 기준으로 하는 사람',
                '',
                '이 유형은 감정적인 상황에서 비교적 빠르게 반응하는 편입니다. 그래서 분위기가 바뀌거나 갈등이 생기면 감정적으로 영향을 받는 순간이 나타날 수 있습니다.',
                '',
                '다만 다른 사람의 감정을 깊이 공감하는 편은 아닙니다. 대신 상대의 상태나 상황을 인지하고 이해하는 방식에 가깝습니다. 그래서 감정에 오래 머무르기보다는 상황을 파악하려는 모습을 보이는 경우가 많습니다.',
                '',
                '또한 관계에서 약속이나 신뢰, 선과 같은 기준을 비교적 중요하게 생각하는 편입니다. 그래서 감정적인 순간이 있더라도 관계를 판단할 때는 감정보다는 기준이나 원칙을 더 우선해서 보려는 경향이 있습니다.',
                '',
                '그래서 이 유형은 감정적인 반응이 전혀 없는 사람은 아니지만, 결국 관계를 판단할 때는 기준과 원칙을 중심으로 정리하는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 감정 표현은 있지만 기준이 분명한 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        LLM: {
            title: 'LLM 유형',
            body: [
                '감정에는 반응하지만 관계는 상황에 따라 판단하는 사람',
                '',
                '이 유형은 사람의 감정이나 분위기에 비교적 빠르게 반응하는 편입니다. 그래서 관계 속에서 분위기가 바뀌거나 갈등이 생기면 감정적으로 영향을 받는 순간이 나타날 수 있습니다.',
                '',
                '다만 다른 사람의 감정을 깊이 공감하는 편은 아닙니다. 대신 상대의 상태나 상황을 인지하고 이해하는 방식에 가깝습니다. 그래서 감정적인 분위기 속에서도 감정에 오래 머무르기보다는 지금 어떤 상황인지 먼저 보려는 경우가 많습니다.',
                '',
                '또한 관계를 항상 같은 방식으로 유지하려 하기보다는 상황과 관계의 흐름에 따라 거리를 조절하는 편입니다. 어떤 관계에서는 가까워질 수 있고, 어떤 상황에서는 자연스럽게 거리를 두는 선택을 하기도 합니다.',
                '',
                '그래서 이 유형은 감정적인 반응이 나타나더라도 관계 자체는 상황과 판단을 기준으로 정리하는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 감정 표현은 있지만 관계 판단은 현실적인 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        LLL: {
            title: 'LLL 유형',
            body: [
                '감정에는 반응하지만 관계에 크게 묶이지 않는 사람',
                '',
                '이 유형은 분위기나 상황의 변화에 비교적 빠르게 반응하는 편입니다. 그래서 대화나 관계 속에서 감정적인 변화가 생기면 그 분위기에 영향을 받거나 바로 반응하는 모습이 나타날 수 있습니다.',
                '',
                '다만 다른 사람의 감정을 깊이 공감하는 편은 아닙니다. 대신 상대의 상태나 상황을 인지하고 이해하는 방식에 가깝습니다. 그래서 감정적인 상황에서도 감정에 오래 머무르기보다는 상황을 파악하고 넘어가는 경우가 많습니다.',
                '',
                '또한 관계를 일정한 기준이나 책임으로 유지해야 한다고 느끼기보다는 자연스럽게 이어지는 관계를 더 편하게 생각하는 편입니다. 그래서 사람들과 쉽게 어울릴 수 있지만, 동시에 특정 관계에 오래 묶여 있는 것을 부담스럽게 느끼기도 합니다.',
                '',
                '그래서 이 유형은 관계를 무겁게 유지하려 하기보다는 자신의 상태와 상황에 맞게 사람들과 어울리는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 자유롭고 거리감이 가벼운 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        HMM: {
            title: 'HMM 유형',
            body: [
                '감정과 상황을 함께 보며 관계의 흐름을 관리하는 사람',
                '',
                '이 유형은 감정적인 상황에서도 비교적 안정적인 상태를 유지하려는 편입니다. 사람들의 감정이나 분위기를 어느 정도 이해하면서도, 감정에 바로 휩쓸리기보다는 지금 상황이 어떻게 흘러가고 있는지 함께 보려는 경우가 많습니다.',
                '',
                '또한 관계에서 강한 원칙이나 기준을 밀어붙이기보다는 상황과 관계의 흐름을 보며 태도를 조절하는 편입니다. 그래서 어떤 관계에서는 적극적으로 참여하고, 어떤 상황에서는 한 발 물러나며 균형을 맞추기도 합니다.',
                '',
                '이 유형은 관계 속에서 감정이나 기준 한쪽으로 치우치기보다는 지금 상황에 맞는 방식으로 관계의 흐름을 조절하려는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 상황을 보며 관계의 균형을 맞추는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        MMM: {
            title: 'MMM 유형',
            body: [
                '사람들과 자연스럽게 어울리는 균형형',
                '',
                '이 유형은 감정과 상황을 모두 어느 정도 이해하면서 사람들과 어울리는 편입니다. 감정적인 상황에서도 크게 흔들리지 않지만 그렇다고 완전히 거리 두는 유형도 아닙니다.',
                '',
                '또한 관계에서 강한 원칙을 세우기보다는 사람들과의 관계를 자연스럽게 이어가는 편입니다. 그래서 특별히 관계를 이끌거나 통제하려 하기보다는 그때그때 상황에 맞게 사람들과 어울리는 모습을 보일 때가 많습니다.',
                '',
                '이 유형은 관계 속에서 특별히 튀는 방식으로 움직이기보다는 무리 없이 사람들과 어울리며 관계를 유지하는 모습을 보이는 경우가 많습니다.',
                '',
                '주변에서는 이 사람을 편하게 어울릴 수 있는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
        MML: {
            title: 'MML 유형',
            body: [
                '사람들과 편안한 거리를 유지하며 어울리는 사람',
                '',
                '이 유형은 감정적인 상황에서도 어느 정도 영향을 받기는 하지만, 감정에 깊이 들어가기보다는 상황을 보며 반응하는 편입니다.',
                '',
                '또한 관계에서 강한 기준이나 책임을 느끼기보다는 자연스럽고 부담 없는 관계를 선호하는 경우가 많습니다. 그래서 사람들과 어울리는 것은 어렵지 않지만, 관계가 너무 무겁거나 책임이 커질 때는 거리를 두는 선택을 하기도 합니다.',
                '',
                '이 유형은 관계 속에서 깊이 관여하기보다는 편안한 거리에서 사람들과 어울리는 모습을 보일 때가 많습니다.',
                '',
                '주변에서는 이 사람을 편안하게 어울리지만 부담을 주지 않는 사람으로 느끼는 경우가 많습니다.',
            ].join('\n'),
        },
    };
    const SOCIAL_LABEL_BY_SEB = {
        // S 낮음 (차분한 표현)
        LLL: '조용하지만 편안한 사람',
        LLM: '차분하고 기본이 잘 잡힌 사람',
        LLH: '묵직하게 신뢰를 주는 사람',
        LML: '차분하지만 상황을 잘 보는 사람',
        LMM: '조용하고 안정적인 사람',
        LMH: '차분하지만 믿고 맡길 수 있는 사람',
        LHL: '말은 적지만 이해력이 좋은 사람',
        LHM: '조용히 공감해주는 사람',
        LHH: '깊이 이해하고 신뢰되는 사람',

        // S 중간 (자연스러운 표현)
        MLL: '편하게 어울릴 수 있는 사람',
        MLM: '부담 없이 편한 사람',
        MLH: '기준이 분명한 든든한 사람',
        MML: '자연스럽게 어울리는 사람',
        MMM: '균형 잡힌 안정적인 사람',
        MMH: '믿음직하고 안정적인 사람',
        MHL: '사람들과 잘 맞는 사람',
        MHM: '따뜻하고 편안한 사람',
        MHH: '사람을 잘 챙기는 사람',

        // S 높음 (표현이 풍부)
        HLL: '솔직하고 에너지가 있는 사람',
        HLM: '감정 표현이 자연스러운 사람',
        HLH: '솔직하면서도 책임감 있는 사람',
        HML: '분위기를 밝게 만드는 사람',
        HMM: '활발하고 편하게 다가오는 사람',
        HMH: '따뜻하면서도 믿음직한 사람',
        HHL: '감정 교류가 풍부한 사람',
        HHM: '따뜻하게 사람을 대하는 사람',
        HHH: '사람을 진심으로 챙기는 사람',
    };
    const toLevel = (pct) => {
        const n = Number(pct || 0);
        if (n >= 67) return 'H';
        if (n >= 34) return 'M';
        return 'L';
    };
    const getSebCode = (sScore, eScore, bScore) => `${toLevel(sScore)}${toLevel(eScore)}${toLevel(bScore)}`;
    const buildSFormationHint = (sScore) => {
        if (sScore >= 67) {
            return [
                'S High가 형성되는 환경',
                '',
                '핵심: 감정을 드러내면 손해였던 환경',
                '',
                '이 사람들은 보통 이런 환경을 경험했을 가능성이 있습니다.',
                '- 감정을 드러내면 상황이 더 커지는 환경',
                '- 침착해야 문제를 해결할 수 있었던 환경',
                '- 주변에서 감정을 통제하는 태도가 중요했던 환경',
                '',
                '그래서 자연스럽게',
                '감정 → 바로 표현',
                '이 아니라',
                '감정 → 참고 → 상황 정리',
                '이 방식이 익숙해집니다.',
            ].join('\n');
        }
        if (sScore >= 34) {
            return [
                'S Mid가 형성되는 환경',
                '',
                '핵심: 감정을 표현해도 괜찮았던 환경',
                '',
                '이 사람들은 보통 이런 환경을 경험했을 가능성이 있습니다.',
                '- 감정을 표현해도 크게 문제되지 않았던 환경',
                '- 갈등이 생겨도 대화로 해결되는 환경',
                '- 감정 표현이 자연스러웠던 관계',
                '',
                '그래서',
                '감정 → 표현 → 정리',
                '이 패턴이 됩니다.',
                '',
                '감정을 표현하는 것이 자연스러웠고, 동시에 상황을 정리하는 경험도 함께 있었던 환경에서 이런 성향이 형성되었을 가능성이 있습니다.',
                '또한 상황에 따라 감정을 강제로 조절하는 역할을 오래 하면, 컨디션이 낮을 때 감정이 한 번에 터질 수 있습니다.',
            ].join('\n');
        }
        return [
            'S Low가 형성되는 환경',
            '',
            '핵심: 감정이 바로 행동이 되는 환경',
            '',
            '이 사람들은 보통 이런 환경을 경험했을 가능성이 있습니다.',
            '- 감정을 숨길 필요가 없었던 환경',
            '- 분위기나 감정이 바로 표현되는 환경',
            '- 감정 반응이 빠른 사람들과의 관계',
            '',
            '그래서',
            '감정 → 바로 행동',
            '패턴이 됩니다.',
            '',
            '감정을 바로 표현하고 반응하는 것이 자연스러웠던 환경에서 이런 성향이 형성되었을 가능성이 있습니다.',
        ].join('\n');
    };

    const buildSRoleBlock = (sScore) => {
        if (sScore >= 67) {
            return {
                title: 'S High 사람들이 맡았을 가능성이 높은 역할',
                roles: ['정리하는 사람', '중재하는 사람', '버티는 사람'],
                desc: '관계에서 갈등이 생기면 감정적으로 반응하기보다 상황을 정리하거나 중재하려는 역할을 맡는 경우가 많습니다.',
                meanings: [
                    { role: '상황정리', meaning: '문제 → 정리 역할입니다. 갈등이 생기면 먼저 무슨 일이 생겼는지, 무엇이 문제인지부터 정리합니다. “잠깐, 지금 뭐 때문에 싸우는지부터 정리해보자.”처럼 반응합니다. 즉 감정에 바로 반응하기보다 상황을 분석하고 정리하는 문제 해결형 역할입니다.' },
                    { role: '중재', meaning: '사람 ↔ 사람 사이를 연결하는 역할입니다. 갈등이 생기면 두 사람의 입장을 듣고 이어주려는 경향이 있습니다. “너는 이런 뜻이었고 쟤는 이렇게 받아들인 것 같아.”처럼 사람 사이 번역기 역할을 합니다.' },
                ],
            };
        }
        if (sScore >= 34) {
            return {
                title: 'S Mid 사람들이 맡았을 가능성이 높은 역할',
                roles: ['조율하는 사람', '분위기를 맞추는 사람', '상황에 맞게 움직이는 사람'],
                desc: '관계 상황에 따라 분위기를 조율하거나 균형을 맞추는 역할을 하는 경우가 많습니다.',
                meanings: [
                    { role: '조율', meaning: '분위기에 맞추는 역할입니다. 문제를 바로 해결하려 하기보다 상황 분위기에 맞게 움직입니다. 분위기가 안 좋으면 조용해지고, 분위기가 풀리면 같이 풀리는 식으로 반응합니다. 즉, 상황 → 반응 패턴입니다.' },
                    { role: '균형', meaning: '한쪽으로 치우치지 않고 감정과 상황을 함께 봅니다. “네 말도 이해되고 저쪽 말도 이해돼.”처럼 감정 + 상황을 같이 고려하는 역할입니다.' },
                ],
            };
        }
        return {
            title: 'S Low 사람들이 맡았을 가능성이 높은 역할',
            roles: ['감정을 표현하는 사람', '분위기를 움직이는 사람', '감정을 드러내는 사람'],
            desc: '관계에서 감정을 직접 표현하거나 분위기를 움직이는 역할을 하는 경우가 많습니다.',
            meanings: [
                { role: '감정표현', meaning: '감정을 바로 드러냄' },
                { role: '분위기변화', meaning: '관계 분위기를 움직임' },
            ],
        };
    };

    const buildChoiceThinkingText = (sScore) => {
        if (sScore >= 67) {
            return [
                '선택 뒤에 있는 사고 방식 (S High)',
                '',
                'S 성향이 높은 사람들은 감정 상황에서',
                '관계가 더 흔들리지 않는 방향을 먼저 고려하는 경향이 있습니다.',
                '',
                '갈등이 생기면 감정이 올라오는 순간에도',
                '보통 이런 생각이 먼저 떠오를 가능성이 있습니다.',
                '',
                '지금 감정대로 반응하면 상황이 더 커질 수도 있다',
                '',
                '일단 상황을 정리하는 것이 먼저일 수 있다',
                '',
                '감정보다는 문제를 해결하는 것이 중요하다',
                '',
                '그래서 감정이 올라와도 바로 반응하기보다',
                '상황을 정리하거나 문제를 해결하려는 선택을 하는 경우가 많습니다.',
                '',
                'S High가 우선하는 기준',
                '관계 안정',
                '',
                '이 사람들은 보통 이렇게 생각합니다.',
                '',
                '지금 중요한 것은 감정보다',
                '관계가 더 흔들리지 않게 만드는 것이다.',
                '',
                '그래서 감정을 바로 표현하기보다',
                '관계를 안정시키는 선택을 먼저 하려는 경향이 있습니다.',
                '',
                '예를 들어',
                '',
                '잠깐 대화를 멈추고 상황을 정리한다',
                '',
                '서로 감정이 가라앉은 뒤 이야기하려 한다',
                '',
                '문제의 원인을 먼저 찾으려 한다',
                '',
                '문제 해결',
                '',
                '갈등이 생겼을 때 보통 이런 질문을 합니다.',
                '',
                '지금 이 문제를 어떻게 정리할 수 있을까?',
                '',
                '그래서 감정을 그대로 표현하기보다',
                '문제를 해결할 방법을 먼저 찾으려는 사고가 나타나는 경우가 많습니다.',
                '',
                '관계 유지',
                '',
                '이 사람들은 보통 관계가 크게 흔들리는 것을 원하지 않습니다.',
                '',
                '그래서 감정이 올라오는 상황에서도',
                '관계를 계속 이어갈 수 있는 방향을 먼저 생각하는 경우가 많습니다.',
                '',
                '예를 들어',
                '',
                '감정 표현을 잠시 미룬다',
                '',
                '상황을 정리하려 한다',
                '',
                '대화를 통해 문제를 해결하려 한다',
                '',
                '한 문장으로 정리',
                '',
                'S High 사람들의 기본 사고는 보통 이렇습니다.',
                '',
                '지금 감정보다',
                '관계를 안정시키는 것이 먼저다.',
                '',
                '그래서 감정이 올라와도',
                '바로 반응하기보다 상황을 정리하고 문제를 해결하려는 선택을 하는 경우가 많습니다.',
                '',
            ].join('\n');
        }
        if (sScore >= 34) {
            return [
                'S Mid가 우선하는 기준',
                '감정 + 상황을 같이 본다',
                '',
                '이 사람들은 보통 이렇게 생각합니다.',
                '내가 느끼는 감정도 중요하지만,',
                '지금 상황이 어떻게 돌아가고 있는지도 중요하다.',
                '',
                '그래서 감정을 무시하지도 않고',
                '감정만으로 판단하지도 않습니다.',
                '',
                '예를 들어',
                '',
                '서운한 감정이 들면 그 감정을 인정하지만',
                '',
                '동시에 왜 그런 일이 생겼는지도 생각해 봅니다.',
                '',
                '서로 이해하려 한다',
                '',
                '갈등 상황에서 보통 이런 질문을 합니다.',
                '',
                '내가 이렇게 느낀 이유는 뭐지?',
                '상대는 왜 그렇게 행동했을까?',
                '',
                '그래서 감정을 바로 터뜨리기보다',
                '상대의 입장도 함께 이해하려는 방향으로 생각이 움직일 때가 많습니다.',
                '',
                '관계의 흐름을 본다',
                '',
                '이 사람들은 단순히 “누가 맞는지”보다',
                '지금 이 관계가 어디로 가고 있는지를 생각하는 경우가 많습니다.',
                '',
                '예를 들어',
                '',
                '지금 감정대로 말하면 관계가 더 나빠질까',
                '',
                '조금 정리해서 이야기하는 게 더 나을까',
                '',
                '같은 질문을 하면서',
                '관계 흐름이 크게 흔들리지 않는 선택을 하려는 경향이 있습니다.',
                '',
                '한 문장으로 정리',
                '',
                'S Mid 사람들의 기본 사고는 보통 이렇습니다.',
                '',
                '지금 감정도 중요하지만,',
                '상황과 관계도 함께 고려해야 한다.',
                '',
                '그래서 감정에만 반응하지도 않고',
                '상황만 보고 판단하지도 않는 균형적인 선택을 하는 경우가 많습니다.',
            ].join('\n');
        }
        return [
            '선택 뒤에 있는 사고 방식 (S Low)',
            '',
            'S 성향이 낮은 사람들은 감정 상황에서',
            '지금 느끼는 감정을 먼저 고려하는 경향이 있습니다.',
            '',
            '갈등이 생기면 감정이 올라오는 순간 보통 이런 생각이 먼저 떠오를 가능성이 있습니다.',
            '',
            '지금 내가 느끼는 감정이 중요하다',
            '',
            '이 감정을 그냥 넘기면 안 된다',
            '',
            '지금 느끼는 걸 표현해야 한다',
            '',
            '그래서 상황을 먼저 정리하기보다',
            '지금 느끼는 감정을 표현하거나 반응하는 선택을 하는 경우가 많습니다.',
            '',
            'S Low가 우선하는 기준',
            '감정 표현',
            '',
            '이 사람들은 보통 이렇게 생각합니다.',
            '',
            '지금 느끼는 감정은 그냥 넘길 수 있는 것이 아니다.',
            '',
            '그래서 감정을 억누르기보다',
            '감정을 표현하거나 확인하려는 선택을 하는 경우가 많습니다.',
            '',
            '예를 들어',
            '',
            '서운한 감정을 바로 말한다',
            '',
            '화가 나면 바로 반응한다',
            '',
            '감정이 정리되기 전이라도 표현한다',
            '',
            '진짜 감정',
            '',
            '이 사람들은 관계에서',
            '진짜 감정이 숨겨지는 것을 불편하게 느끼는 경우가 많습니다.',
            '',
            '그래서 상황을 정리하기보다',
            '감정을 먼저 드러내는 것이 더 솔직한 행동이라고 느낄 수 있습니다.',
            '',
            '예를 들어',
            '',
            '감정을 숨기지 않으려 한다',
            '',
            '감정이 올라오면 바로 이야기한다',
            '',
            '분위기보다 감정이 중요하다고 느낀다',
            '',
            '감정 확인',
            '',
            '이 사람들은 감정 상황에서 보통 이런 질문을 합니다.',
            '',
            '지금 내가 느끼는 이 감정은 무엇일까?',
            '',
            '그래서 감정을 정리하기보다',
            '감정을 먼저 확인하고 표현하려는 방향으로 생각이 움직이는 경우가 많습니다.',
            '',
            '한 문장으로 정리',
            '',
            'S Low 사람들의 기본 사고는 보통 이렇습니다.',
            '',
            '지금 느끼는 감정이 중요하다.',
            '',
            '그래서 상황을 먼저 정리하기보다',
            '감정을 표현하거나 반응하는 선택을 하는 경우가 많습니다.',
        ].join('\n');
    };

    const buildIdealText = (sScore) => {
        if (sScore >= 67) {
            return [
                'S High — 관계를 안정시키는 것이 먼저인 사람',
                '',
                '이 사람들은 감정이 올라오는 순간에도 보통',
                '“이 관계가 더 흔들리면 안 된다”를 먼저 생각하는 편입니다.',
                '',
                '즉 이들에게 갈등은 단순히 기분이 상한 일이 아니라,',
                '관계 전체가 불안정해질 수 있는 사건으로 느껴질 가능성이 높습니다.',
                '그래서 감정이 올라와도 그 감정을 바로 밖으로 내보내기보다,',
                '일단 상황을 정리하고 문제를 작게 만들고 안정시키려는 방향으로 움직입니다.',
                '',
                '이 사람들이 우선하는 것은 보통 이런 것들입니다.',
                '- 관계가 더 나빠지지 않는 것',
                '- 감정이 과열되지 않는 것',
                '- 문제를 해결 가능한 상태로 바꾸는 것',
                '- 관계를 계속 이어갈 수 있는 구조를 만드는 것',
                '',
                '그래서 실제로는 이런 선택을 하기 쉽습니다.',
                '- 바로 반응하지 않고 잠깐 멈춘다',
                '- 감정보다 “무슨 일이 있었는지”부터 정리한다',
                '- 상대와 나 둘 다 진정된 뒤 이야기하려 한다',
                '- 갈등의 원인을 찾고 정리하려 한다',
                '',
                '이 사람에게는 감정 표현보다',
                '관계를 다시 안정권으로 돌려놓는 것이 더 중요한 경우가 많습니다.',
                '',
                '그래서 장점은 분명합니다.',
                '관계가 크게 흔들릴 때 중심을 잡는 사람이 될 수 있습니다.',
                '감정 폭발로 일을 더 키우기보다, 상황을 수습하고 문제를 해결하는 쪽으로 움직입니다.',
                '주변 사람들은 이런 사람을 보고 보통',
                '“차분하다”, “버팀목 같다”, “믿을 수 있다”고 느끼기 쉽습니다.',
                '',
                '하지만 이 욕구가 너무 강하면 단점도 생깁니다.',
                '관계를 안정시키려는 마음이 커질수록',
                '내 감정은 뒤로 밀리고, 상대 감정까지 내가 관리하려는 태도가 나올 수 있습니다.',
                '문제를 해결하는 데 익숙하다 보니',
                '상대 입장에서는 “내 감정보다 해결이 먼저인 사람”처럼 느껴질 수도 있습니다.',
                '',
                '즉 이 사람의 핵심은',
                '감정보다 관계의 안정과 지속 가능성을 먼저 지키려는 사람이라는 데 있습니다.',
            ].join('\n');
        }
        if (sScore >= 34) {
            return [
                'S Mid — 서로의 입장이 한쪽으로 치우치지 않기를 바라는 사람',
                '',
                '이 사람들은 S High처럼 무조건 관계 안정만 보지도 않고,',
                'S Low처럼 감정 표현을 최우선으로 두지도 않습니다.',
                '이들이 더 중요하게 생각하는 건',
                '“한쪽만 참거나, 한쪽만 밀어붙이는 상태가 되지 않는 것”입니다.',
                '',
                '즉 이들에게 중요한 것은',
                '관계가 유지되느냐 하나만이 아니라,',
                '그 관계가 서로에게 어느 정도 공정하고 균형 잡혀 있느냅니다.',
                '',
                '이들은 감정이 올라오는 상황에서도 보통 이렇게 생각할 가능성이 높습니다.',
                '- 내 감정도 중요하다',
                '- 상대 감정도 무시하면 안 된다',
                '- 그렇다고 감정만으로 결정하면 안 된다',
                '- 지금은 서로 어느 쪽으로 너무 치우쳐 있지 않은지가 중요하다',
                '',
                '그래서 이 사람들이 우선하는 것은 이런 것들입니다.',
                '- 서로의 입장이 같이 고려되는 것',
                '- 감정과 현실이 동시에 반영되는 것',
                '- 한쪽만 손해 보거나 한쪽만 참지 않는 것',
                '- 지금 상황에 맞는 적절한 균형을 찾는 것',
                '',
                '실제로는 이런 선택이 많습니다.',
                '- 내 감정도 말하지만 상대 말도 들어본다',
                '- 바로 결론 내리기보다 대화를 이어가 본다',
                '- 누가 맞냐보다 “지금 뭐가 균형 잡힌 선택인지”를 본다',
                '- 감정적으로 반응했다가도 다시 정리하려 한다',
                '',
                '이 사람은 관계를 무조건 붙잡는 사람도 아니고,',
                '쉽게 끊는 사람도 아닙니다.',
                '핵심은 치우치지 않으려는 것입니다.',
                '',
                '그래서 장점은 유연함입니다.',
                '상황에 따라 감정도 보고 현실도 봅니다.',
                '상대 입장을 완전히 무시하지도 않고,',
                '내 감정만 앞세우지도 않습니다.',
                '주변 사람들은 이런 사람을',
                '“무난하다”, “조율이 된다”, “같이 지내기 편하다”고 느끼기 쉽습니다.',
                '',
                '하지만 단점도 있습니다.',
                '균형을 너무 중시하면 오히려',
                '입장이 모호한 사람, 결정을 미루는 사람, 확실히 선을 긋지 않는 사람처럼 보일 수 있습니다.',
                '또한 누구 한쪽으로 확실히 기울지 않으려 하다 보니',
                '상대는 “그래서 네 진짜 생각은 뭐야?”라고 느낄 수도 있습니다.',
                '',
                '즉 이 사람의 핵심은',
                '관계를 유지하는 것 자체보다, 서로의 입장과 감정이 한쪽으로 치우치지 않는 상태를 지키려는 사람이라는 데 있습니다.',
            ].join('\n');
        }
        return [
            'S Low — 지금 느끼는 감정이 무시되지 않는 것이 먼저인 사람',
            '',
            '이 사람들은 갈등이나 감정 상황에서',
            '관계 안정이나 균형보다 먼저',
            '“지금 내가 느끼는 감정이 진짜다”를 중요하게 생각하는 경우가 많습니다.',
            '',
            '즉 이들에게 감정은 나중에 정리할 대상이라기보다',
            '지금 이 상황을 보여주는 가장 중요한 신호에 가깝습니다.',
            '그래서 감정이 올라오면 그 감정을 바로 느끼고,',
            '그 감정을 표현하거나 행동으로 드러내는 쪽으로 갈 가능성이 높습니다.',
            '',
            '이들이 우선하는 것은 보통 이런 것들입니다.',
            '- 내 감정이 무시되지 않는 것',
            '- 지금 느끼는 불편함이 제대로 표현되는 것',
            '- 관계를 위해 감정을 억지로 눌러두지 않는 것',
            '- 진짜 감정을 바로 확인하는 것',
            '',
            '그래서 실제로는 이런 선택이 나오기 쉽습니다.',
            '- 서운하면 바로 서운하다고 한다',
            '- 화가 나면 거리 두기나 감정 표현이 먼저 나온다',
            '- 상황을 정리하기 전에 감정 반응이 먼저 나온다',
            '- 상대 반응과 분위기에 바로 영향을 받는다',
            '',
            '이 사람은 감정에 휘둘리는 사람이라기보다,',
            '감정을 가장 중요한 정보로 받아들이는 사람에 가깝습니다.',
            '',
            '그래서 장점도 분명합니다.',
            '자기 감정을 속이지 않습니다.',
            '관계에서 느끼는 불편함이나 서운함을 빨리 알아차립니다.',
            '감정이 죽어 있는 관계보다, 살아 있는 감정이 오가는 관계를 더 진짜라고 느낍니다.',
            '주변 사람들은 이런 사람을',
            '“솔직하다”, “감정이 분명하다”, “반응이 살아 있다”고 볼 수 있습니다.',
            '',
            '하지만 단점도 있습니다.',
            '감정이 중요한 만큼',
            '관계를 유지하기 위한 정리나 거리 두기보다 반응이 먼저 나올 수 있습니다.',
            '상대 입장에서는 예민하거나, 감정 기복이 크거나, 상황을 더 키우는 사람처럼 느껴질 수도 있습니다.',
            '또 내 감정이 강하게 올라올 때는',
            '상대 감정이나 전체 상황을 충분히 보기 전에 반응해버릴 가능성도 있습니다.',
            '',
            '즉 이 사람의 핵심은',
            '관계를 지키는 것보다 지금 느끼는 감정이 제대로 표현되고 확인되는 것을 먼저 지키려는 사람이라는 데 있습니다.',
        ].join('\n');
    };

    const buildEFormationHint = (eScore) => {
        if (eScore >= 67) {
            return [
                'E High 형성 배경 (감정을 함께 느끼는 방식)',
                '',
                '이 성향은 보통 타인의 감정에 자연스럽게 반응하는 경험이 많았던 관계 환경에서 형성될 가능성이 있습니다.',
                '',
                '예를 들면',
                '',
                '주변 사람들의 감정이 관계 분위기에 큰 영향을 주던 환경',
                '',
                '누군가 힘들어하면 그 감정을 함께 느끼는 경험이 많았던 관계',
                '',
                '감정을 공유하거나 공감하는 대화가 자주 있었던 환경',
                '',
                '이런 환경에서는 자연스럽게 이런 학습이 이루어질 수 있습니다.',
                '',
                '사람을 이해하려면 먼저 그 감정을 느껴봐야 한다.',
                '',
                '그래서 상대의 감정을 보면',
                '그 감정이 자신의 감정에도 영향을 주는 방식이 익숙해질 수 있습니다.',
                '',
                '타인의 감정에 자연스럽게 반응하는 경험이 반복되면서 사람의 감정을 함께 느끼며 이해하는 방식이 형성되었을 가능성이 있습니다.',
            ].join('\n');
        }
        if (eScore >= 34) {
            return [
                'E Mid 형성 배경 (감정을 이해하는 방식)',
                '',
                '이 성향은 보통 감정을 느끼는 경험과 감정을 설명하는 경험을 모두 겪은 환경에서 형성될 가능성이 있습니다.',
                '',
                '예를 들면',
                '',
                '감정을 표현하는 관계 경험도 있었고',
                '',
                '동시에 왜 그런 감정을 느끼는지 이야기하는 환경',
                '',
                '그래서 이런 사고 방식이 만들어질 수 있습니다.',
                '',
                '이 사람은 지금 이런 감정을 느끼는 것 같다. 왜 그런 걸까?',
                '',
                '즉 감정을 느끼기도 하고',
                '그 감정을 이해하려는 사고가 함께 작동합니다.',
                '',
                '감정을 느끼는 경험과 감정을 이해하는 경험을 함께 하면서 사람의 감정을 이해하려는 방식이 형성되었을 가능성이 있습니다.',
            ].join('\n');
        }
        return [
            'E Low 형성 배경 (감정을 정보로 보는 방식)',
            '',
            '이 성향은 보통 감정보다 상황이나 이유를 먼저 이해하는 경험이 많았던 환경에서 형성될 가능성이 있습니다.',
            '',
            '예를 들면',
            '',
            '감정보다 문제 해결이 중요했던 관계 경험',
            '',
            '감정 표현보다 상황 설명이 먼저였던 환경',
            '',
            '논리나 판단을 중심으로 대화하는 관계',
            '',
            '이런 환경에서는 자연스럽게 이런 사고 방식이 만들어질 수 있습니다.',
            '',
            '왜 이런 일이 생겼지?',
            '',
            '그래서 감정을 느끼기보다',
            '감정을 상황을 이해하는 정보로 해석하는 방식이 형성될 수 있습니다.',
            '',
            '감정보다 상황을 이해하거나 문제의 이유를 설명하는 경험이 반복되면서 사람의 감정을 상황의 정보로 해석하는 방식이 형성되었을 가능성이 있습니다.',
        ].join('\n');
    };

    const buildEChoiceThinkingText = (eScore) => {
        if (eScore >= 67) {
            return [
                'E High — 감정 중심 사고',
                '',
                '이 사람들은 사람을 이해할 때 감정 상태를 먼저 떠올리는 사고 방식을 가진 경우가 많습니다.',
                '',
                '어떤 일이 생기면 보통 이런 생각이 먼저 나옵니다.',
                '',
                '이 사람이 지금 어떤 감정을 느끼고 있을까?',
                '',
                '그래서 사람을 이해할 때',
                '',
                '표정',
                '',
                '말투',
                '',
                '분위기',
                '',
                '반응',
                '',
                '같은 감정 신호를 먼저 읽습니다.',
                '',
                '이 사람들의 사고 흐름은 보통 이렇게 움직입니다.',
                '',
                '감정 → 상황 이해',
                '',
                '즉 상대 감정을 먼저 보고',
                '그 감정이 왜 생겼는지 상황을 이해하려 합니다.',
                '',
                '핵심',
                '',
                '사람 이해 = 감정 중심',
            ].join('\n');
        }
        if (eScore >= 34) {
            return [
                'E Mid — 감정 + 상황 혼합 사고',
                '',
                '이 사람들은 사람을 이해할 때 감정과 상황을 동시에 고려하는 사고 방식을 가진 경우가 많습니다.',
                '',
                '어떤 일이 생기면 이런 생각이 함께 나옵니다.',
                '',
                '이 사람이 왜 이런 감정을 느끼지?',
                '무슨 일이 있었던 걸까?',
                '',
                '그래서',
                '',
                '감정도 보고',
                '',
                '상황도 봅니다.',
                '',
                '이 사람들의 사고 흐름은 보통 이렇게 움직입니다.',
                '',
                '감정 + 상황 → 이해',
                '',
                '핵심',
                '',
                '사람 이해 = 감정 + 상황',
            ].join('\n');
        }
        return [
            'E Low — 인지 중심 사고',
            '',
            '이 사람들은 사람을 이해할 때 상황이나 이유를 먼저 떠올리는 사고 방식을 가진 경우가 많습니다.',
            '',
            '어떤 일이 생기면 이런 생각이 먼저 나옵니다.',
            '',
            '무슨 일이 있었지?',
            '',
            '그래서',
            '',
            '사건',
            '',
            '이유',
            '',
            '맥락',
            '',
            '같은 상황 정보를 먼저 봅니다.',
            '',
            '이 사람들의 사고 흐름은 보통 이렇게 움직입니다.',
            '',
            '상황 → 감정 이해',
            '',
            '즉 상황을 이해한 뒤',
            '그 감정을 해석하려 합니다.',
            '',
            '핵심',
            '',
            '사람 이해 = 상황 중심',
        ].join('\n');
    };

    const buildEIdealText = (eScore) => {
        if (eScore >= 67) {
            return [
                'E High의 이상향',
                '',
                '이 사람들에게 이상적인 관계는',
                '',
                '감정을 이해하고 공감할 수 있는 관계',
                '',
                '입니다.',
                '',
                '이 사람들은 관계에서 이런 상태를 원합니다.',
                '',
                '서로 감정을 솔직하게 표현하는 것',
                '',
                '상대가 내 감정을 이해해주는 것',
                '',
                '내가 상대 감정을 이해할 수 있는 것',
                '',
                '이들에게 관계의 이상은 보통 이런 모습입니다.',
                '',
                '서로의 감정을 이해하고 공감할 수 있는 관계',
                '',
                '그래서 관계에서',
                '',
                '감정 공유',
                '',
                '공감',
                '',
                '정서적 연결',
                '',
                '을 중요하게 느끼는 경우가 많습니다.',
            ].join('\n');
        }
        if (eScore >= 34) {
            return [
                'E Mid의 이상향',
                '',
                '이 사람들에게 이상적인 관계는',
                '',
                '감정과 현실이 균형 잡힌 관계',
                '',
                '입니다.',
                '',
                '이들은 관계에서 이런 상태를 원합니다.',
                '',
                '감정도 이해되지만',
                '',
                '상황도 현실적으로 돌아가는 것',
                '',
                '즉',
                '',
                '서로 이해하면서도 관계가 안정적으로 유지되는 상태',
                '',
                '그래서',
                '',
                '공감',
                '',
                '현실 판단',
                '',
                '관계 흐름',
                '',
                '이 세 가지가 함께 있는 관계를 선호합니다.',
            ].join('\n');
        }
        return [
            'E Low의 이상향',
            '',
            '이 사람들에게 이상적인 관계는',
            '',
            '이해 가능하고 명확한 관계',
            '',
            '입니다.',
            '',
            '이 사람들은 관계에서 이런 상태를 원합니다.',
            '',
            '상황이 명확한 것',
            '',
            '서로 역할이 분명한 것',
            '',
            '감정보다 관계 구조가 안정적인 것',
            '',
            '이들에게 관계의 이상은 보통',
            '',
            '감정보다 관계가 명확하게 작동하는 상태',
            '',
            '그래서',
            '',
            '명확한 대화',
            '',
            '문제 해결',
            '',
            '논리적 이해',
            '',
            '를 중요하게 느끼는 경우가 많습니다.',
        ].join('\n');
    };

    const buildBFormationHint = (bScore) => {
        if (bScore >= 67) {
            return [
                'B High 형성 경험 (약속/원칙 중심)',
                '',
                '이 성향은 보통 약속이나 규칙이 관계에 큰 영향을 미쳤던 경험에서 형성될 가능성이 있습니다.',
                '',
                '예를 들면',
                '',
                '약속을 어겼을 때 크게 혼났던 경험',
                '',
                '“약속은 반드시 지켜야 한다”는 기준이 강했던 환경',
                '',
                '누군가가 약속을 지키지 않아 관계가 틀어졌던 경험',
                '',
                '역할이나 책임이 분명했던 관계 경험',
                '',
                '이런 경험을 하면 자연스럽게 이런 생각이 생깁니다.',
                '',
                '약속은 지켜야 하는 것이다.',
                '',
                '그래서 관계에서도',
                '',
                '약속',
                '',
                '책임',
                '',
                '역할',
                '',
                '을 중요하게 보는 기준이 형성될 수 있습니다.',
                '',
                '결과 설명 예',
                '',
                '약속을 어겼을 때 관계가 크게 흔들렸던 경험이나 약속을 중요하게 여기는 환경을 통해 관계에는 지켜야 할 기준이 있다는 인식이 형성되었을 가능성이 있습니다.',
            ].join('\n');
        }
        if (bScore >= 34) {
            return [
                'B Mid 형성 경험 (유연한 기준)',
                '',
                '이 성향은 보통 약속도 중요하지만 상황에 따라 바뀔 수 있는 경험에서 형성될 가능성이 있습니다.',
                '',
                '예를 들면',
                '',
                '약속을 지키는 것도 중요하지만 상황 때문에 바뀌는 경험',
                '',
                '서로 조율하면서 관계를 유지했던 경험',
                '',
                '규칙이 있지만 유연하게 조정되던 관계',
                '',
                '이런 경험을 하면 자연스럽게 이런 생각이 생깁니다.',
                '',
                '약속도 중요하지만 상황도 고려해야 한다.',
                '',
                '그래서 관계에서도',
                '',
                '약속을 중요하게 보지만',
                '',
                '상황에 따라 조정하는 태도가 형성될 수 있습니다.',
                '',
                '결과 설명 예',
                '',
                '약속을 지키는 경험과 상황에 따라 조정하는 경험을 함께 하면서 관계의 기준을 유연하게 바라보는 방식이 형성되었을 가능성이 있습니다.',
            ].join('\n');
        }
        return [
            'B Low 형성 경험 (자율 중심)',
            '',
            '이 성향은 보통 약속이나 규칙이 크게 중요하지 않았던 관계 경험에서 형성될 가능성이 있습니다.',
            '',
            '예를 들면',
            '',
            '약속이 자주 바뀌는 관계',
            '',
            '상황에 따라 계획이 계속 달라졌던 경험',
            '',
            '관계보다 개인 선택이 더 중요했던 환경',
            '',
            '규칙보다 편한 대로 관계가 이어졌던 경험',
            '',
            '이런 경험을 하면 자연스럽게 이런 생각이 생깁니다.',
            '',
            '약속보다 상황이 더 중요할 수 있다.',
            '',
            '그래서 관계에서도',
            '',
            '약속보다 선택',
            '',
            '규칙보다 자유',
            '',
            '를 중요하게 보는 기준이 형성될 수 있습니다.',
            '',
            '결과 설명 예',
            '',
            '약속이나 규칙이 크게 중요하지 않았던 관계 경험을 통해 관계를 보다 자유롭게 바라보는 방식이 형성되었을 가능성이 있습니다.',
        ].join('\n');
    };

    const buildBChoiceThinkingText = (bScore) => {
        if (bScore >= 67) {
            return [
                'B High 사고방식 (원칙 중심)',
                '',
                '이 사람들은 선택을 할 때 보통 기준이나 약속을 먼저 떠올린다.',
                '',
                '머릿속 사고 흐름은 보통 이런 식이다.',
                '',
                '이건 약속한 거잖아.',
                '약속이면 지켜야 하는 거 아닌가?',
                '',
                '그래서 판단 기준이',
                '',
                '약속',
                '',
                '책임',
                '',
                '역할',
                '',
                '기준',
                '',
                '같은 것들이다.',
                '',
                '예를 들면 이런 생각이 나온다.',
                '',
                '“우리가 이렇게 하기로 했잖아.”',
                '',
                '“약속은 지켜야지.”',
                '',
                '“관계에는 기본적인 기준이 있어야 한다.”',
                '',
                '즉 이 사람에게 중요한 것은',
                '',
                '기준 → 행동',
                '',
                '이다.',
            ].join('\n');
        }
        if (bScore >= 34) {
            return [
                'B Mid 사고방식 (상황 중심)',
                '',
                '이 사람들은 약속도 보지만 상황을 같이 본다.',
                '',
                '머릿속 사고 흐름은 보통 이런 식이다.',
                '',
                '약속은 했지만 지금 상황도 봐야 하지 않을까?',
                '',
                '그래서 판단 기준이',
                '',
                '약속',
                '',
                '상황',
                '',
                '서로 입장',
                '',
                '을 동시에 고려한다.',
                '',
                '예를 들면 이런 생각이 나온다.',
                '',
                '“약속이긴 한데 상황이 좀 애매하다.”',
                '',
                '“이건 서로 이야기해보면 될 것 같다.”',
                '',
                '“지금 상황이면 바꿔도 괜찮지 않을까?”',
                '',
                '즉 이 사람에게 중요한 것은',
                '',
                '기준 + 상황 → 행동',
                '',
                '이다.',
            ].join('\n');
        }
        return [
            'B Low 사고방식 (자율 중심)',
            '',
            '이 사람들은 선택을 할 때 개인의 선택이나 상황을 먼저 본다.',
            '',
            '머릿속 사고 흐름은 보통 이런 식이다.',
            '',
            '지금 상황이 더 중요한 것 아닌가?',
            '',
            '그래서 판단 기준이',
            '',
            '현재 상황',
            '',
            '개인 선택',
            '',
            '자유',
            '',
            '이다.',
            '',
            '예를 들면 이런 생각이 나온다.',
            '',
            '“지금 상황이 바뀌었는데.”',
            '',
            '“그때 약속했지만 지금은 다르다.”',
            '',
            '“굳이 그걸 지켜야 하나?”',
            '',
            '즉 이 사람에게 중요한 것은',
            '',
            '상황 → 행동',
            '',
            '이다.',
        ].join('\n');
    };

    const buildBIdealText = (bScore) => {
        if (bScore >= 67) {
            return [
                'B High — 원칙 중심 관계',
                '',
                '이 사람들에게 이상적인 관계는',
                '',
                '기준이 분명한 관계',
                '',
                '이다.',
                '',
                '이 사람들은 관계에서 보통 이런 상태를 원한다.',
                '',
                '서로 약속을 지키는 관계',
                '',
                '책임과 역할이 분명한 관계',
                '',
                '기준이 흔들리지 않는 관계',
                '',
                '이들에게 관계의 안정은 보통',
                '',
                '신뢰 = 약속을 지키는 것',
                '',
                '이다.',
                '',
                '그래서 이상적인 관계는',
                '',
                '서로 약속을 지키고 책임을 나누는 관계',
                '',
                '이다.',
            ].join('\n');
        }
        if (bScore >= 34) {
            return [
                'B Mid — 유연한 관계',
                '',
                '이 사람들에게 이상적인 관계는',
                '',
                '유연하게 조정되는 관계',
                '',
                '이다.',
                '',
                '이 사람들은 보통',
                '',
                '약속도 중요하지만',
                '',
                '상황도 고려하는 관계',
                '',
                '를 원한다.',
                '',
                '그래서 이상적인 관계는',
                '',
                '서로 기준을 맞추면서 조율할 수 있는 관계',
                '',
                '이다.',
                '',
                '즉',
                '',
                '기준 + 상황',
                '',
                '이 같이 작동하는 관계다.',
            ].join('\n');
        }
        return [
            'B Low — 자유 중심 관계',
            '',
            '이 사람들에게 이상적인 관계는',
            '',
            '자율적인 관계',
            '',
            '이다.',
            '',
            '이 사람들은 관계에서 보통',
            '',
            '개인 선택',
            '',
            '자유',
            '',
            '강요 없는 관계',
            '',
            '를 중요하게 느낀다.',
            '',
            '그래서 이상적인 관계는',
            '',
            '서로를 통제하지 않는 관계',
            '',
            '이다.',
            '',
            '즉',
            '',
            '관계 = 선택',
            '',
            '이다.',
        ].join('\n');
    };

    const buildSocialFormationHint = (sScore, eScore, bScore) => {
        const socialTilt = Math.round((Number(eScore || 50) + Number(bScore || 50)) / 2);
        if (socialTilt >= 67) {
            return [
                '사회적 관계 형성 배경 (연결 민감)',
                '',
                '사람 사이 분위기와 신호가 관계에 큰 영향을 주던 환경에서',
                '사회적 반응이 빠르게 형성될 가능성이 있습니다.',
                '',
                '그래서',
                '상대 기분/거리/약속 변화에 민감하게 반응하는 패턴이 생길 수 있습니다.',
            ].join('\n');
        }
        if (socialTilt >= 34) {
            return [
                '사회적 관계 형성 배경 (균형 조율)',
                '',
                '감정 신호도 보면서 동시에 관계 규칙과 상황도 함께 보던 환경에서',
                '사회적 관계를 유연하게 조율하는 방식이 형성될 가능성이 있습니다.',
                '',
                '그래서',
                '상황에 맞게 거리, 말투, 기준을 조정하는 패턴이 생길 수 있습니다.',
            ].join('\n');
        }
        return [
            '사회적 관계 형성 배경 (자기 기준 중심)',
            '',
            '감정보다 맥락/규칙/개인 선택이 더 중요했던 환경에서',
            '사회적 관계를 “필요한 만큼만” 유지하는 방식이 형성될 가능성이 있습니다.',
            '',
            '그래서',
            '관계에서 감정 소모를 줄이고, 명확한 경계를 선호하는 패턴이 생길 수 있습니다.',
        ].join('\n');
    };

    const buildSocialChoiceThinkingText = (sScore, eScore, bScore) => {
        const socialTilt = Math.round((Number(eScore || 50) + Number(bScore || 50)) / 2);
        if (socialTilt >= 67) {
            return [
                '사회적 관계 사고 방식 (신호 중심)',
                '',
                '상대의 감정 신호와 관계 분위기를 먼저 읽고',
                '그에 맞춰 반응을 선택하는 경향이 있습니다.',
                '',
                '핵심',
                '관계 신호 → 반응',
            ].join('\n');
        }
        if (socialTilt >= 34) {
            return [
                '사회적 관계 사고 방식 (조율 중심)',
                '',
                '내 감정/상대 감정/관계 기준을 함께 보고',
                '지금 상황에서 무리 없는 균형점을 찾는 경향이 있습니다.',
                '',
                '핵심',
                '감정 + 기준 + 상황 → 조율',
            ].join('\n');
        }
        return [
            '사회적 관계 사고 방식 (경계 중심)',
            '',
            '상대 신호보다 맥락과 기준을 먼저 보고',
            '내가 감당 가능한 선에서 관계를 운영하려는 경향이 있습니다.',
            '',
            '핵심',
            '기준 → 거리',
        ].join('\n');
    };

    const buildSocialIdealText = (sScore, eScore, bScore) => {
        const socialTilt = Math.round((Number(eScore || 50) + Number(bScore || 50)) / 2);
        if (socialTilt >= 67) {
            return [
                '사회적 관계 이상향',
                '',
                '감정 교류가 충분하고, 신호가 빠르게 오가며,',
                '관계가 끊기지 않게 자주 연결되는 형태를 편하게 느끼는 경우가 많습니다.',
            ].join('\n');
        }
        if (socialTilt >= 34) {
            return [
                '사회적 관계 이상향',
                '',
                '가깝게 연결되되 서로의 기준과 상황을 존중하며,',
                '필요할 때 조율로 풀리는 관계를 편하게 느끼는 경우가 많습니다.',
            ].join('\n');
        }
        return [
            '사회적 관계 이상향',
            '',
            '과도한 감정 소모 없이, 역할과 경계가 명확하고,',
            '필요한 만큼만 연결되는 편안한 관계를 선호하는 경우가 많습니다.',
        ].join('\n');
    };

    const buildSocialUnifiedText = (sScore, eScore, bScore) => {
        const code = getSebCode(sScore, eScore, bScore);
        const label = SOCIAL_LABEL_BY_SEB[code] || '';
        return [
            label ? `한 줄 요약: ${label}` : '',
            label ? '' : '',
            '1. 형성 배경',
            buildSocialFormationHint(sScore, eScore, bScore),
            '',
            '2. 사고 방식',
            buildSocialChoiceThinkingText(sScore, eScore, bScore),
            '',
            '3. 이상향',
            buildSocialIdealText(sScore, eScore, bScore),
        ].join('\n');
    };

    const scoreToBand = (pct) => {
        const n = Number(pct || 0);
        if (n >= 67) return 'high';
        if (n >= 34) return 'mid';
        return 'low';
    };

    const AxisVenn = ({ axisKey }) => {
        const axis = AXIS_WORDS[axisKey];
        if (!axis || !sebProfile) return null;
        const pct = sebProfile.percent[axis.scoreKey];
        const band = scoreToBand(pct);

        const data = axis.levels[band] || axis.levels.mid;

        return (
            <div style={styles.vennBlock}>
                <div style={styles.vennHeaderRow}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <div style={styles.vennTitle}>{axis.title}</div>
                        <span style={styles.axisLevelBadge}>{data.label}</span>
                    </div>
                    <div style={styles.vennScore}>
                        점수 <span style={{ color: '#fcd34d', fontWeight: 900 }}>{pct}%</span>
                    </div>
                </div>
                <div style={styles.vennChips} aria-label={`${axis.title} 단어 목록`}>
                    {data.adjectives.map((w) => (
                        <span key={w} style={styles.vennChip}>{w}</span>
                    ))}
                </div>
            </div>
        );
    };

    const AxisWordsAll = () => {
        if (!sebProfile) return null;
        const axes = [
            { key: 'stability', label: 'S축 단어' },
            { key: 'empathy', label: 'E축 단어' },
            { key: 'belief', label: 'B축 단어' },
        ];

        return (
            <div style={styles.wordsAllWrap}>
                {axes.map((a) => {
                    const axis = AXIS_WORDS[a.key];
                    const pct = sebProfile.percent[axis.scoreKey];
                    const band = scoreToBand(pct);
                    const level = axis.levels[band] || axis.levels.mid;
                    return (
                        <div key={a.key} style={styles.wordsAxisCard}>
                            <div style={styles.wordsAxisHeader}>
                                <div style={styles.wordsAxisTitle}>{axis.title}</div>
                                <div style={styles.wordsAxisMeta}>
                                    <span style={styles.axisLevelBadge}>{level.label}</span>
                                    <span style={styles.wordsAxisScore}>{pct}%</span>
                                </div>
                            </div>
                            <div style={styles.vennChips}>
                                {level.adjectives.map((w) => (
                                    <span key={`${a.key}_${band}_${w}`} style={styles.vennChip}>{w}</span>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    useEffect(() => {
        if (stats.mega && stats.mega.payload && stats.mega.payload.answers) {
            setSebProfile(getSebProfileFromAnswers(stats.mega.payload.answers));
        }
    }, [stats.mega]);

    if (loading) return <div style={styles.container}>{t('common.loading')}</div>;
    if (lang === 'en') return <EnglishPaidReport profile={sebProfile} onBack={() => navigate('/mypage')} />;

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <button onClick={() => navigate('/mypage')} style={styles.backButton}>← {t('common.back')}</button>
                <h1 style={styles.headerTitle}>Premium Report: New Canvas</h1>
                <p style={styles.headerSubtitle}>심층 분석 리포트가 준비되었습니다. 새로운 내용을 아래에 구성해주세요.</p>

                {sebProfile && (
                    <div style={styles.mainCard}>
                        {(() => {
                            const sRole = buildSRoleBlock(sebProfile.percent.S);
                            return (
                                <>
                        <div style={styles.badge}>PREMIUM RAW DATA</div>
                        <div style={{ padding: '1rem', background: 'rgba(15,23,42,0.5)', borderRadius: '12px' }}>
                            <div style={{ marginBottom: '1rem' }}>
                                <strong>Type Code:</strong> <span style={{ color: '#fcd34d' }}>{sebProfile.typeCode}</span>
                            </div>
                            <div style={{ display: 'grid', gap: '0.8rem' }}>
                                {['S', 'E', 'F'].map(axis => (
                                    <div key={axis} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 50px', alignItems: 'center', gap: '1rem' }}>
                                        <span>{axis === 'F' ? 'Belief (B)' : axis === 'S' ? 'Stability' : 'Empathy'}</span>
                                        <div style={{ height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                                            <div style={{ width: `${sebProfile.percent[axis]}%`, height: '100%', background: axis === 'S' ? '#fbbf24' : axis === 'E' ? '#60a5fa' : '#f472b6' }} />
                                        </div>
                                        <span style={{ textAlign: 'right' }}>{sebProfile.percent[axis]}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={styles.deepReportCard}>
                            <div style={styles.deepReportTitle}>심층 레포트</div>
                            <div style={styles.axisTabs}>
                                <button
                                    type="button"
                                    style={activeAxis === 'stability' ? styles.axisButtonActive : styles.axisButton}
                                    onClick={() => { setActiveAxis('stability'); setActiveDeepSection('1'); }}
                                >
                                    Stability
                                </button>
                                <button
                                    type="button"
                                    style={activeAxis === 'empathy' ? styles.axisButtonActive : styles.axisButton}
                                    onClick={() => { setActiveAxis('empathy'); setActiveDeepSection('1'); }}
                                >
                                    Empathy
                                </button>
                                <button
                                    type="button"
                                    style={activeAxis === 'belief' ? styles.axisButtonActive : styles.axisButton}
                                    onClick={() => { setActiveAxis('belief'); setActiveDeepSection('1'); }}
                                >
                                    Belief
                                </button>
                                <button
                                    type="button"
                                    style={activeAxis === 'social' ? styles.axisButtonActive : styles.axisButton}
                                    onClick={() => { setActiveAxis('social'); setActiveDeepSection('1'); setSocialSummaryOpen(false); }}
                                >
                                    사회적 관계
                                </button>
                            </div>

                            {activeAxis !== 'social' && (
                                <AxisVenn axisKey={activeAxis} />
                            )}

                            {activeAxis !== 'social' && (
                                <div style={styles.reportIndex}>
                                    <button
                                        type="button"
                                        style={activeDeepSection === '1' ? styles.indexChipActive : styles.indexChipButton}
                                        onClick={() => setActiveDeepSection('1')}
                                    >
                                        1 형성 배경
                                    </button>
                                    <button
                                        type="button"
                                        style={activeDeepSection === '2' ? styles.indexChipActive : styles.indexChipButton}
                                        onClick={() => setActiveDeepSection('2')}
                                    >
                                        2 사고 방식
                                    </button>
                                    <button
                                        type="button"
                                        style={activeDeepSection === '3' ? styles.indexChipActive : styles.indexChipButton}
                                        onClick={() => setActiveDeepSection('3')}
                                    >
                                        3 이상향
                                    </button>
                                </div>
                            )}

                            {activeAxis === 'stability' && activeDeepSection === '1' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>1</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={styles.deepReportHeading}>S축이 왜 이렇게 형성됐는지</h3>
                                        <p style={styles.deepReportText}>{buildSFormationHint(sebProfile.percent.S)}</p>
                                        <div style={styles.roleCard}>
                                            <h4 style={styles.roleCardTitle}>{sRole.title}</h4>
                                            <p style={styles.roleLabel}>핵심 역할</p>
                                            <div style={styles.roleList}>
                                                {sRole.roles.map((role) => (
                                                    <span key={role} style={styles.roleChip}>{role}</span>
                                                ))}
                                            </div>
                                            <p style={styles.deepReportText}>{sRole.desc}</p>
                                            <div style={styles.roleMeaningCard}>
                                                <p style={styles.roleMeaningTitle}>역할 의미</p>
                                                <div style={styles.roleMeaningTable}>
                                                    {sRole.meanings.map((item) => (
                                                        <div key={item.role} style={styles.roleMeaningRow}>
                                                            <span style={styles.roleMeaningRole}>{item.role}</span>
                                                            <span style={styles.roleMeaningMeaning}>{item.meaning}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeAxis === 'stability' && activeDeepSection === '2' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>2</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={styles.deepReportHeading}>선택 뒤에 있는 사고 방식</h3>
                                        <p style={styles.deepReportText}>{buildChoiceThinkingText(sebProfile.percent.S)}</p>
                                    </div>
                                </div>
                            )}

                            {activeAxis === 'stability' && activeDeepSection === '3' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>3</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={styles.deepReportHeading}>이상향</h3>
                                        <p style={styles.deepReportText}>{buildIdealText(sebProfile.percent.S)}</p>
                                    </div>
                                </div>
                            )}

                            {activeAxis === 'empathy' && activeDeepSection === '1' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>1</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={styles.deepReportHeading}>E축이 왜 이렇게 형성됐는지</h3>
                                        <p style={styles.deepReportText}>{buildEFormationHint(sebProfile.percent.E)}</p>
                                    </div>
                                </div>
                            )}

                            {activeAxis === 'empathy' && activeDeepSection === '2' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>2</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={styles.deepReportHeading}>선택 뒤에 있는 사고 방식</h3>
                                        <p style={styles.deepReportText}>{buildEChoiceThinkingText(sebProfile.percent.E)}</p>
                                    </div>
                                </div>
                            )}

                            {activeAxis === 'empathy' && activeDeepSection === '3' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>3</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={styles.deepReportHeading}>이상향</h3>
                                        <p style={styles.deepReportText}>{buildEIdealText(sebProfile.percent.E)}</p>
                                    </div>
                                </div>
                            )}

                            {activeAxis === 'belief' && activeDeepSection === '1' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>1</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={styles.deepReportHeading}>B축이 왜 이렇게 형성됐는지</h3>
                                        <p style={styles.deepReportText}>{buildBFormationHint(sebProfile.percent.F)}</p>
                                    </div>
                                </div>
                            )}

                            {activeAxis === 'belief' && activeDeepSection === '2' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>2</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={styles.deepReportHeading}>선택 뒤에 있는 사고 방식</h3>
                                        <p style={styles.deepReportText}>{buildBChoiceThinkingText(sebProfile.percent.F)}</p>
                                    </div>
                                </div>
                            )}

                            {activeAxis === 'belief' && activeDeepSection === '3' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>3</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={styles.deepReportHeading}>이상향</h3>
                                        <p style={styles.deepReportText}>{buildBIdealText(sebProfile.percent.F)}</p>
                                    </div>
                                </div>
                            )}

                            {activeAxis === 'social' && (
                                <div style={styles.deepReportSection}>
                                    <div style={styles.deepReportBadge}>S</div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={{ ...styles.deepReportHeading, margin: 0 }}>사회적 관계</h3>
                                        {SOCIAL_TYPE_SUMMARY_BY_CODE[sebProfile.typeCode] ? (
                                            <div style={styles.socialToggleRow}>
                                                <button
                                                    type="button"
                                                    style={styles.socialToggleBtn}
                                                    onClick={() => setSocialSummaryOpen(v => !v)}
                                                >
                                                    {socialSummaryOpen ? '핵심 요약 접기' : '핵심 요약 보기'}
                                                </button>
                                            </div>
                                        ) : null}
                                        {SOCIAL_TYPE_SUMMARY_BY_CODE[sebProfile.typeCode] && socialSummaryOpen ? (
                                            <div style={styles.socialSummaryCard}>
                                                <div style={styles.socialSummaryTitle}>
                                                    {SOCIAL_TYPE_SUMMARY_BY_CODE[sebProfile.typeCode].title}
                                                </div>
                                                <p style={styles.socialSummaryText}>
                                                    {SOCIAL_TYPE_SUMMARY_BY_CODE[sebProfile.typeCode].body}
                                                </p>
                                                <div style={{ marginTop: '0.9rem' }}>
                                                    <h4 style={styles.wordsAllTitle}>축 단어 모아보기</h4>
                                                    <AxisWordsAll />
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            )}
                        </div>
                                </>
                            );
                        })()}
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        background: 'radial-gradient(circle at top, #1e293b 0%, #0f172a 45%)',
        color: '#f8fafc',
        padding: '2rem 1rem'
    },
    content: {
        maxWidth: '920px',
        margin: '0 auto'
    },
    backButton: {
        background: 'none',
        border: 'none',
        color: '#94a3b8',
        cursor: 'pointer',
        fontSize: '1rem',
        marginBottom: '2rem'
    },
    headerTitle: {
        fontSize: '2.5rem',
        fontWeight: 800,
        marginBottom: '0.5rem',
        background: 'linear-gradient(to right, #60a5fa, #f472b6)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
    },
    headerSubtitle: {
        color: '#94a3b8',
        fontSize: '1.1rem',
        marginBottom: '3rem'
    },
    mainCard: {
        background: 'rgba(30, 41, 59, 0.7)',
        borderRadius: '24px',
        padding: '2rem',
        border: '1px solid rgba(148, 163, 184, 0.16)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 18px 40px rgba(2, 6, 23, 0.35)'
    },
    badge: {
        display: 'inline-block',
        padding: '0.25rem 0.75rem',
        borderRadius: '999px',
        background: 'rgba(96, 165, 250, 0.1)',
        color: '#60a5fa',
        fontSize: '0.75rem',
        fontWeight: 700,
        marginBottom: '1.5rem',
        border: '1px solid rgba(96, 165, 250, 0.2)'
    },
    deepReportCard: {
        marginTop: '1.2rem',
        padding: '1.1rem',
        background: 'rgba(2, 6, 23, 0.5)',
        borderRadius: '14px',
        border: '1px solid rgba(148, 163, 184, 0.24)',
    },
    deepReportTitle: {
        fontSize: '0.9rem',
        color: '#bfdbfe',
        fontWeight: 800,
        marginBottom: '0.6rem',
    },
    axisTabs: {
        display: 'flex',
        gap: '0.45rem',
        marginBottom: '0.75rem',
    },
    axisButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.3rem 0.7rem',
        borderRadius: '999px',
        border: '1px solid rgba(147, 197, 253, 0.28)',
        background: 'rgba(15, 23, 42, 0.55)',
        color: '#dbeafe',
        fontSize: '0.8rem',
        fontWeight: 700,
        cursor: 'pointer',
    },
    axisButtonActive: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.3rem 0.7rem',
        borderRadius: '999px',
        border: '1px solid rgba(147, 197, 253, 0.42)',
        background: 'rgba(96, 165, 250, 0.22)',
        color: '#ffffff',
        fontSize: '0.8rem',
        fontWeight: 800,
        cursor: 'pointer',
    },
    reportIndex: {
        display: 'flex',
        gap: '0.45rem',
        flexWrap: 'wrap',
        marginBottom: '0.75rem',
    },
    indexChipButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.2rem 0.5rem',
        borderRadius: '999px',
        background: 'rgba(96, 165, 250, 0.12)',
        border: '1px solid rgba(147, 197, 253, 0.25)',
        color: '#dbeafe',
        fontSize: '0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
    },
    indexChipActive: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.2rem 0.5rem',
        borderRadius: '999px',
        background: 'rgba(59, 130, 246, 0.3)',
        border: '1px solid rgba(147, 197, 253, 0.45)',
        color: '#ffffff',
        fontSize: '0.75rem',
        fontWeight: 800,
        cursor: 'pointer',
    },
    vennBlock: {
        marginTop: '0.75rem',
        marginBottom: '0.8rem',
        padding: '0.8rem',
        borderRadius: '12px',
        background: 'rgba(15, 23, 42, 0.42)',
        border: '1px solid rgba(148, 163, 184, 0.18)',
    },
    vennHeaderRow: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: '0.6rem',
        flexWrap: 'wrap',
        marginBottom: '0.65rem',
    },
    vennTitle: {
        fontSize: '0.95rem',
        fontWeight: 900,
        color: '#e2e8f0',
        letterSpacing: '-0.01em',
    },
    axisLevelBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.16rem 0.5rem',
        borderRadius: '999px',
        background: 'rgba(252, 211, 77, 0.10)',
        border: '1px solid rgba(252, 211, 77, 0.30)',
        color: '#fde68a',
        fontSize: '0.75rem',
        fontWeight: 900,
        whiteSpace: 'nowrap',
    },
    wordsAllTitle: {
        margin: '0 0 0.55rem 0',
        fontSize: '0.92rem',
        fontWeight: 900,
        color: '#e2e8f0',
        letterSpacing: '-0.01em',
    },
    wordsAllWrap: {
        display: 'grid',
        gap: '0.8rem',
    },
    wordsAxisCard: {
        padding: '0.75rem',
        borderRadius: '12px',
        background: 'rgba(2, 6, 23, 0.38)',
        border: '1px solid rgba(148, 163, 184, 0.18)',
    },
    wordsAxisHeader: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: '0.6rem',
        flexWrap: 'wrap',
        marginBottom: '0.6rem',
    },
    wordsAxisTitle: {
        fontSize: '0.88rem',
        fontWeight: 900,
        color: '#f8fafc',
        letterSpacing: '-0.01em',
    },
    wordsAxisMeta: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
    },
    wordsAxisScore: {
        fontSize: '0.78rem',
        color: '#94a3b8',
        fontWeight: 800,
    },
    vennScore: {
        fontSize: '0.8rem',
        color: '#94a3b8',
        fontWeight: 700,
    },
    vennChips: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))',
        gap: '0.45rem',
        overflow: 'visible',
        padding: '0.15rem',
        alignContent: 'start',
    },
    vennChip: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.36rem 0.55rem',
        borderRadius: '12px',
        background: 'rgba(15, 23, 42, 0.78)',
        border: '1px solid rgba(226, 232, 240, 0.22)',
        color: '#f8fafc',
        fontSize: '0.86rem',
        fontWeight: 900,
        letterSpacing: '-0.01em',
        whiteSpace: 'nowrap',
    },
    deepReportSection: {
        display: 'flex',
        gap: '0.7rem',
        alignItems: 'flex-start',
        background: 'rgba(15, 23, 42, 0.55)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        borderRadius: '12px',
        padding: '0.8rem',
        marginBottom: '0.7rem',
    },
    deepReportBadge: {
        width: '26px',
        height: '26px',
        borderRadius: '999px',
        background: 'rgba(59, 130, 246, 0.2)',
        color: '#dbeafe',
        fontSize: '0.8rem',
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: '1px',
    },
    deepReportHeading: {
        margin: '0 0 0.4rem 0',
        fontSize: '1rem',
        color: '#f8fafc',
        fontWeight: 700,
    },
    deepReportText: {
        margin: 0,
        color: '#cbd5e1',
        fontSize: '0.92rem',
        lineHeight: 1.68,
        whiteSpace: 'pre-line',
    },
    socialSummaryCard: {
        marginTop: '0.6rem',
        padding: '0.75rem',
        borderRadius: '12px',
        background: 'rgba(2, 6, 23, 0.38)',
        border: '1px solid rgba(148, 163, 184, 0.18)',
    },
    socialToggleRow: {
        marginTop: '0.55rem',
        marginBottom: '0.2rem',
        display: 'flex',
        justifyContent: 'flex-start',
    },
    socialToggleBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.22rem 0.6rem',
        borderRadius: '999px',
        background: 'rgba(59, 130, 246, 0.16)',
        border: '1px solid rgba(96, 165, 250, 0.28)',
        color: '#dbeafe',
        fontSize: '0.78rem',
        fontWeight: 900,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    socialSummaryTitle: {
        fontSize: '0.9rem',
        fontWeight: 900,
        color: '#f8fafc',
        marginBottom: '0.35rem',
        letterSpacing: '-0.01em',
    },
    socialSummaryText: {
        margin: 0,
        color: '#cbd5e1',
        fontSize: '0.9rem',
        lineHeight: 1.7,
        whiteSpace: 'pre-line',
        fontWeight: 600,
    },
    roleCard: {
        marginTop: '0.85rem',
        padding: '0.75rem',
        background: 'rgba(2, 6, 23, 0.42)',
        border: '1px solid rgba(148, 163, 184, 0.24)',
        borderRadius: '10px',
    },
    roleCardTitle: {
        margin: '0 0 0.45rem 0',
        color: '#f8fafc',
        fontSize: '0.9rem',
        fontWeight: 700,
    },
    roleLabel: {
        margin: '0 0 0.35rem 0',
        color: '#93c5fd',
        fontSize: '0.8rem',
        fontWeight: 700,
    },
    roleList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.35rem',
        marginBottom: '0.55rem',
    },
    roleChip: {
        display: 'inline-flex',
        alignItems: 'center',
        background: 'rgba(59, 130, 246, 0.15)',
        border: '1px solid rgba(96, 165, 250, 0.3)',
        color: '#dbeafe',
        borderRadius: '999px',
        padding: '0.2rem 0.5rem',
        fontSize: '0.78rem',
        fontWeight: 600,
    },
    roleMeaningCard: {
        marginTop: '0.7rem',
        background: 'rgba(2, 6, 23, 0.45)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        borderRadius: '8px',
        padding: '0.6rem',
    },
    roleMeaningTitle: {
        margin: '0 0 0.45rem 0',
        color: '#bfdbfe',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    roleMeaningTable: {
        display: 'grid',
        gap: '0.3rem',
    },
    roleMeaningRow: {
        display: 'grid',
        gridTemplateColumns: '88px 1fr',
        gap: '0.4rem',
        alignItems: 'center',
        padding: '0.3rem 0.35rem',
        borderRadius: '6px',
        background: 'rgba(15, 23, 42, 0.45)',
    },
    roleMeaningRole: {
        color: '#e2e8f0',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    roleMeaningMeaning: {
        color: '#94a3b8',
        fontSize: '0.78rem',
        lineHeight: 1.35,
    },
};

export default PaidVersion;
